"use client";

import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNotification } from "../contexts/NotificationContext";
import { Canvas } from "@react-three/fiber";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import OrthesisViewer from "../components/OrthesisViewer";
import LoginChecker from "../components/LoginCkecker";
import { STLExporter } from "three/examples/jsm/exporters/STLExporter";
import { useTranslation } from "react-i18next";
import {
  CloudUpload,
  Check,
  CircleDot,
  RefreshCcw,
  Download,
  Bone,
  Scissors,
  Layers,
  FileDown,
  ArrowLeft,
  ArrowRight,
  Hand,
  Ruler,
  Settings2,
  SplitSquareHorizontal,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

// =============== WORKFLOW STEP COMPONENT ===============
const WorkflowStep = ({ step, index, isCompleted, isActive, onClick }) => {
  const { t } = useTranslation();
  return (
    <div
      className={`relative flex items-center group ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      <div className={`shrink-0 w-10 h-10 rounded-full border-2 flex items-center justify-center z-10 transition-all duration-300 ${
        isCompleted
          ? "bg-[#0f7699]/10 dark:bg-[#4fc3f7]/15 border-[#0f7699] dark:border-[#4fc3f7] text-[#0f7699] dark:text-[#4fc3f7]"
          : isActive
            ? "bg-[#0f7699]/5 dark:bg-[#4fc3f7]/10 border-[#0f7699] dark:border-[#4fc3f7] text-[#0f7699] dark:text-[#4fc3f7] ring-4 ring-[#0f7699]/20 dark:ring-[#4fc3f7]/20"
            : "bg-slate-50 dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-400 dark:text-slate-600"
      }`}>
        {isCompleted ? (
          <Check size={18} />
        ) : isActive ? (
          <CircleDot size={18} className="animate-pulse" />
        ) : (
          <span className="text-xs font-semibold">{index + 1}</span>
        )}
      </div>
      <div className="ml-4 lg:ml-5 whitespace-nowrap lg:whitespace-normal">
        <p className={`text-sm font-medium transition-colors duration-200 ${
          isActive
            ? "text-slate-900 dark:text-white"
            : isCompleted
              ? "text-[#0f7699] dark:text-[#4fc3f7]"
              : "text-slate-400 dark:text-slate-600"
        }`}>
          {step.label}
        </p>
        <span className={`text-xs ${
          isActive
            ? "text-[#0f7699] dark:text-[#4fc3f7] font-medium"
            : isCompleted
              ? "text-[#0f7699]/70 dark:text-[#4fc3f7]/70"
              : "text-slate-400 dark:text-slate-700"
        }`}>
          {isCompleted ? t("orthesisEditor.statusCompleted") : isActive ? t("orthesisEditor.statusInProgress") : t("orthesisEditor.statusPending")}
        </span>
      </div>
    </div>
  );
};

// =============== SLIDER COMPONENT ===============
const ParamSlider = ({ label, value, onChange, min, max, step = 0.1, unit = "mm", disabled = false }) => (
  <div className="space-y-1.5">
    <div className="flex items-center justify-between">
      <label className="text-xs font-bold text-slate-700 dark:text-slate-200">{label}</label>
      <span className="text-xs font-mono font-bold text-[#0f7699] dark:text-[#4fc3f7] tabular-nums">
        {typeof value === 'number' ? value.toFixed(1) : value} {unit}
      </span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={e => onChange(parseFloat(e.target.value))}
      disabled={disabled}
      className="w-full h-1.5 rounded-full appearance-none cursor-pointer accent-[#0f7699] dark:accent-[#4fc3f7] bg-slate-200 dark:bg-slate-700 disabled:opacity-40"
    />
    <div className="flex justify-between text-[9px] text-slate-400 dark:text-slate-600">
      <span>{min}</span>
      <span>{max}</span>
    </div>
  </div>
);

// =============== POINT TILE — compact A/B/C/D click-to-place button ===============
// Used across all step panels so the same compact layout works on mobile,
// tablet and desktop without scrolling. Each tile shows: status dot, short
// label, and an action icon (⊕ = empty, ↻ = set, … = currently placing).
const POINT_TILE_COLORS = {
  amber:  { dot: 'bg-amber-500',  borderActive: 'border-amber-500 bg-amber-500/20 text-amber-400',   borderSet: 'border-amber-400 text-amber-500',   hover: 'hover:border-amber-400' },
  purple: { dot: 'bg-purple-500', borderActive: 'border-purple-500 bg-purple-500/20 text-purple-400', borderSet: 'border-purple-400 text-purple-500', hover: 'hover:border-purple-400' },
  orange: { dot: 'bg-orange-500', borderActive: 'border-orange-500 bg-orange-500/20 text-orange-400', borderSet: 'border-orange-400 text-orange-500', hover: 'hover:border-orange-400' },
  red:    { dot: 'bg-red-500',    borderActive: 'border-red-500 bg-red-500/20 text-red-400',          borderSet: 'border-red-400 text-red-500',       hover: 'hover:border-red-400' },
  blue:   { dot: 'bg-blue-500',   borderActive: 'border-blue-500 bg-blue-500/20 text-blue-400',       borderSet: 'border-blue-400 text-blue-500',     hover: 'hover:border-blue-400' },
  green:  { dot: 'bg-green-500',  borderActive: 'border-green-500 bg-green-500/20 text-green-400',    borderSet: 'border-green-400 text-green-500',   hover: 'hover:border-green-400' },
};
const PointTile = ({ label, isSet, isActive, color = 'amber', onClick }) => {
  const c = POINT_TILE_COLORS[color] || POINT_TILE_COLORS.amber;
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border text-left transition-all ${
        isActive
          ? `${c.borderActive} animate-pulse`
          : isSet
            ? `${c.borderSet} bg-white dark:bg-slate-800`
            : `border-slate-300 dark:border-slate-700 text-slate-500 bg-white dark:bg-slate-800 ${c.hover}`
      }`}
    >
      <span className={`w-2 h-2 rounded-full shrink-0 ${isSet ? c.dot : 'bg-slate-300 dark:bg-slate-600'}`} />
      <span className="text-[10px] font-bold flex-1 leading-tight">{label}</span>
      <span className="text-[10px] font-bold ml-auto">{isActive ? '…' : isSet ? '↻' : '⊕'}</span>
    </button>
  );
};

// =============== EDITOR TILE — IconButton-style for the mobile/tablet panel ===============
// Larger, touch-friendly button matching the Scanbearbeitung (PageModelViewer)
// IconButton pattern: square icon box on top + small label below. Used for both
// click-to-place actions (point setters) and discrete selectors (pattern choice).
// DESKTOP is NOT supposed to use these — wrap in `lg:hidden` at the call site.
const EDITOR_TILE_COLORS = {
  teal:   { active: 'border-[#0f7699] dark:border-[#4fc3f7] bg-[#0f7699]/10 dark:bg-[#4fc3f7]/15 text-[#0f7699] dark:text-[#4fc3f7]' },
  amber:  { active: 'border-amber-500 bg-amber-500/10 text-amber-500' },
  purple: { active: 'border-purple-500 bg-purple-500/10 text-purple-500' },
  orange: { active: 'border-orange-500 bg-orange-500/10 text-orange-500' },
  green:  { active: 'border-green-500 bg-green-500/10 text-green-500' },
  red:    { active: 'border-red-500 bg-red-500/10 text-red-500' },
  blue:   { active: 'border-blue-500 bg-blue-500/10 text-blue-500' },
};
const EditorTile = ({ icon: Icon, label, sublabel, isActive, isSet, isDisabled, color = 'teal', onClick }) => {
  const c = EDITOR_TILE_COLORS[color] || EDITOR_TILE_COLORS.teal;
  const boxClass = isActive
    ? `${c.active} animate-pulse`
    : isSet
      ? c.active
      : 'border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-400 dark:text-slate-500';
  return (
    <button
      onClick={onClick}
      disabled={isDisabled}
      className={`flex flex-col items-center gap-1 transition-all w-full ${isDisabled ? 'opacity-40 cursor-not-allowed' : 'active:scale-95'}`}
    >
      <div className={`w-full h-12 sm:h-14 rounded-xl border-2 flex items-center justify-center transition-colors ${boxClass}`}>
        {Icon && <Icon size={20} />}
      </div>
      <span className="text-[10px] sm:text-[11px] font-bold text-slate-700 dark:text-slate-300 text-center leading-tight">
        {label}
      </span>
      {sublabel && (
        <span className="text-[9px] text-slate-500 dark:text-slate-400 leading-tight text-center">
          {sublabel}
        </span>
      )}
    </button>
  );
};

// =============== EDITOR VALUE TILE — shows numeric value, opens picker on tap ===============
// Slider replacement for mobile/tablet. The icon box becomes a value display.
// Tap opens a touch-friendly bottom-sheet picker. DESKTOP keeps the original
// slider (`hidden lg:block` for slider, `lg:hidden` for this tile).
const EditorValueTile = ({ label, value, unit, sublabel, onClick }) => {
  const c = EDITOR_TILE_COLORS.teal;
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center gap-1 transition-all w-full active:scale-95"
    >
      <div className={`w-full h-12 sm:h-14 rounded-xl border-2 flex flex-col items-center justify-center ${c.active}`}>
        <span className="text-sm sm:text-base font-black leading-none tabular-nums">{value}</span>
        {unit && <span className="text-[8px] sm:text-[9px] font-medium leading-tight opacity-70 mt-0.5">{unit}</span>}
      </div>
      <span className="text-[10px] sm:text-[11px] font-bold text-slate-700 dark:text-slate-300 text-center leading-tight">
        {label}
      </span>
      {sublabel && (
        <span className="text-[9px] text-slate-500 dark:text-slate-400 leading-tight text-center">
          {sublabel}
        </span>
      )}
    </button>
  );
};

// =============== VALUE PICKER SHEET — touch-friendly bottom sheet ===============
// Opens as a bottom sheet on mobile, centered modal on tablet+. Renders via
// portal to escape the editor's `overflow-hidden` chain.
const ValuePickerSheet = ({ open, title, options, value, formatLabel, onSelect, onClose }) => {
  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center bg-slate-900/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-sm max-h-[70vh] flex flex-col shadow-2xl border border-slate-200 dark:border-slate-700"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white dark:bg-slate-800 px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between rounded-t-2xl">
          <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">{title}</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-lg leading-none"
            aria-label="Schließen"
          >
            ×
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {(options || []).map((opt) => {
            const optVal = typeof opt === 'object' && opt !== null && 'value' in opt ? opt.value : opt;
            const optLabel = typeof opt === 'object' && opt !== null && 'label' in opt
              ? opt.label
              : (formatLabel ? formatLabel(optVal) : String(optVal));
            const isSelected = optVal === value;
            return (
              <button
                key={String(optVal)}
                onClick={() => onSelect(optVal)}
                className={`px-3 py-3 rounded-xl text-sm font-bold transition-colors ${
                  isSelected
                    ? 'bg-[#0f7699] dark:bg-[#4fc3f7] text-white dark:text-slate-900 shadow'
                    : 'bg-slate-50 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-600 border border-slate-200 dark:border-slate-600'
                }`}
              >
                {optLabel}
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
};

// Build a list of numeric options spanning [min, max] with given step.
// Used to feed ValuePickerSheet from existing slider min/max/step bounds.
const buildNumericOptions = (min, max, step) => {
  const out = [];
  const decimals = step < 1 ? Math.max(0, -Math.floor(Math.log10(step))) : 0;
  // Avoid float drift by indexing
  const count = Math.round((max - min) / step) + 1;
  for (let i = 0; i < count; i++) {
    const v = parseFloat((min + i * step).toFixed(decimals));
    out.push(v);
  }
  return out;
};

// =============== SECTION CARD COMPONENT ===============
// Tighter padding/spacing on small screens so the panel + nav buttons can fit
// inside the viewport on tablet/mobile without forcing a body scroll.
const SectionCard = ({ title, icon: Icon, children, color = "slate" }) => (
  <div className="bg-white dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-xl p-2.5 sm:p-4 shadow-sm">
    <div className="flex items-center gap-2 mb-2 sm:mb-4">
      <div className={`w-6 h-6 sm:w-7 sm:h-7 rounded-lg flex items-center justify-center ${
        color === 'red' ? 'bg-red-50 dark:bg-red-900/20' :
        color === 'blue' ? 'bg-blue-50 dark:bg-blue-900/20' :
        color === 'green' ? 'bg-green-50 dark:bg-green-900/20' :
        color === 'teal' ? 'bg-[#0f7699]/10 dark:bg-[#4fc3f7]/15' :
        'bg-slate-100 dark:bg-slate-700'
      }`}>
        <Icon size={14} className={
          color === 'red' ? 'text-red-500' :
          color === 'blue' ? 'text-blue-500' :
          color === 'green' ? 'text-green-500' :
          color === 'teal' ? 'text-[#0f7699] dark:text-[#4fc3f7]' :
          'text-slate-500'
        } />
      </div>
      <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{title}</h4>
    </div>
    <div className="space-y-2 sm:space-y-4">
      {children}
    </div>
  </div>
);

// =============== HELPER: Adjust normal by tilt ===============
function adjustNormal(normal, tiltX, tiltZ) {
  let nx = normal.x, ny = normal.y, nz = normal.z;
  if (tiltX) {
    const c = Math.cos(tiltX), s = Math.sin(tiltX);
    const newY = ny * c - nz * s;
    nz = ny * s + nz * c;
    ny = newY;
  }
  if (tiltZ) {
    const c = Math.cos(tiltZ), s = Math.sin(tiltZ);
    const newX = nx * c - ny * s;
    ny = nx * s + ny * c;
    nx = newX;
  }
  const len = Math.sqrt(nx*nx + ny*ny + nz*nz);
  return { x: nx/len, y: ny/len, z: nz/len };
}

// =============== COLLAPSIBLE RING SECTION ===============
const CollapsibleRingSection = ({ title, color, isSet, onPlace, isPlacing, onRemove, children }) => {
  const [expanded, setExpanded] = useState(false);
  const colorBorder = color === 'red' ? 'border-red-500/30' : color === 'blue' ? 'border-blue-500/30' : 'border-green-500/30';
  const colorDot = color === 'red' ? 'bg-red-500' : color === 'blue' ? 'bg-blue-500' : 'bg-green-500';
  const colorText = color === 'red' ? 'text-red-400' : color === 'blue' ? 'text-blue-400' : 'text-green-400';

  return (
    <div className={`bg-white dark:bg-slate-800/80 border ${isSet ? colorBorder : 'border-slate-200 dark:border-slate-700'} rounded-xl overflow-hidden shadow-sm`}>
      {/* Header — always visible */}
      <div className="flex items-center gap-2 px-3 py-2.5">
        <div className={`w-2.5 h-2.5 rounded-full ${isSet ? colorDot : 'bg-slate-300 dark:bg-slate-600'}`} />
        <span className={`text-xs font-bold flex-1 ${isSet ? colorText : 'text-slate-500 dark:text-slate-400'}`}>{title}</span>
        {isSet && (
          <span className="text-[9px] font-bold text-green-500 uppercase">✓</span>
        )}
        {/* Place button (compact) */}
        <button
          onClick={onPlace}
          className={`px-2 py-1 rounded text-[10px] font-bold border transition-all ${
            isPlacing
              ? `${color === 'red' ? 'border-red-500 bg-red-500/20 text-red-400' : color === 'blue' ? 'border-blue-500 bg-blue-500/20 text-blue-400' : 'border-green-500 bg-green-500/20 text-green-400'} animate-pulse`
              : 'border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:border-slate-400'
          }`}
        >
          {isPlacing ? '⊕ ...' : '⊕'}
        </button>
        {/* Expand toggle */}
        {isSet && children && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="px-1.5 py-1 rounded text-[10px] font-bold text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
          >
            {expanded ? '▲' : '▼'}
          </button>
        )}
      </div>

      {/* Expandable slider area */}
      {expanded && isSet && (
        <div className="px-3 pb-3 space-y-3 border-t border-slate-100 dark:border-slate-700 pt-2">
          {children}
          {onRemove && (
            <button
              onClick={onRemove}
              className="w-full px-3 py-1 rounded text-[10px] font-medium border border-red-300 dark:border-red-800 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              Entfernen
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// =============== CLICK-TO-PLACE BUTTON ===============
const ClickPlaceButton = ({ active, onClick, label, color }) => {
  const colorMap = {
    red: { active: 'border-red-500 bg-red-500/20 text-red-400', idle: 'hover:border-red-400' },
    blue: { active: 'border-blue-500 bg-blue-500/20 text-blue-400', idle: 'hover:border-blue-400' },
    green: { active: 'border-green-500 bg-green-500/20 text-green-400', idle: 'hover:border-green-400' },
  };
  const c = colorMap[color] || colorMap.red;

  return (
    <button
      onClick={onClick}
      className={`w-full px-3 py-2 rounded-lg text-[11px] font-bold border transition-all flex items-center justify-center gap-2 ${
        active
          ? `${c.active} animate-pulse`
          : `border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 ${c.idle}`
      }`}
    >
      {active ? `⊕ ${label} — klicken...` : `⊕ ${label}`}
    </button>
  );
};

// =============== MAIN PAGE ===============
function PageOrthesisEditor() {
  const { showNotification: showNotificationRaw } = useNotification();
  // Editor notifications are placement hints that overlap the right-hand
  // toolbar; route them to the bottom-LEFT corner on desktop and HIDE them
  // entirely on mobile (the left stack uses `hidden sm:flex`). All other
  // showNotification consumers across the app keep their default position.
  const showNotification = useCallback(
    (message, type = 'info', duration = 5000) =>
      showNotificationRaw(message, type, duration, { position: 'bottom-left' }),
    [showNotificationRaw]
  );
  const { t } = useTranslation();
  LoginChecker();

  // Check demo-only
  const isDemo = !process.env.REACT_APP_APP_MODE || process.env.REACT_APP_APP_MODE !== 'live';

  const viewerRef = useRef();
  // Synchronously-updated mirror of the rotated wrist/elbow points so the
  // handleModelLoaded callback can read them without taking the state path
  // (state updates are async + would force the callback to be recreated).
  const alignDataRef = useRef({ p1: null, p2: null });

  // File state
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [textureFile, setTextureFile] = useState(null);
  const [modelBounds, setModelBounds] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Workflow
  const [currentStep, setCurrentStep] = useState(0);

  // Alignment parameters
  const [alignPoint1, setAlignPoint1] = useState(null);
  const [alignPoint2, setAlignPoint2] = useState(null);
  const [isAligned, setIsAligned] = useState(false);

  // Alignment markers and rings visibility is handled via props:
  // - showRings={currentStep >= 2} hides rings during alignment
  // - alignPoint1/2 are always passed, viewer shows them only when showAlignMarkers is true

  // Passform parameters — null until model loads
  const [upperCutY, setUpperCutY] = useState(null);
  const [lowerCutY, setLowerCutY] = useState(null);
  const [upperTiltX, setUpperTiltX] = useState(0);
  const [upperTiltZ, setUpperTiltZ] = useState(0);
  const [lowerTiltX, setLowerTiltX] = useState(0);
  const [lowerTiltZ, setLowerTiltZ] = useState(0);
  const [thumbEnabled, setThumbEnabled] = useState(false);
  const [thumbBase, setThumbBase] = useState(null); // Daumenansatz (click point)
  const [thumbTip, setThumbTip] = useState(null);  // Daumenspitze (click point)
  const [thumbRingPos, setThumbRingPos] = useState(0); // 0=base, 1=tip, slider 0-1
  const [thumbTiltX, setThumbTiltX] = useState(0);
  const [thumbTiltZ, setThumbTiltZ] = useState(0);
  const [thumbMode, setThumbMode] = useState('free'); // 'free' | 'fixed'
  const [clickMode, setClickMode] = useState(null); // 'upper' | 'lower' | 'thumbBase' | 'thumbTip' | null
  const [upperClickPoint, setUpperClickPoint] = useState(null);
  const [lowerClickPoint, setLowerClickPoint] = useState(null);
  const [upperPoint, setUpperPoint] = useState(null);
  const [upperNormal, setUpperNormal] = useState(null);
  const [lowerPoint, setLowerPoint] = useState(null);
  const [lowerNormal, setLowerNormal] = useState(null);

  // Material parameters
  const [wallThickness, setWallThickness] = useState(2.5);
  const [skinOffset, setSkinOffset] = useState(0.3);

  // Design parameters
  const [closureCount, setClosureCount] = useState(2);

  // Hole pattern (Step 5 — applied after closures, before export)
  const [holePatternEnabled, setHolePatternEnabled] = useState(false);
  const [holesPerRow, setHolesPerRow] = useState(4);
  const [holeSpacing, setHoleSpacing] = useState(15); // mm axial spacing between hole rows
  // When true, the CSG subtract has been applied to the visible shell geometry.
  // Toggled true on Next-click from Step 5; preview switches from outlines to
  // actual holes through the wall.
  const [holePatternApplied, setHolePatternApplied] = useState(false);
  // Loading state for the heavy hole-pattern work (line drawing + CSG cut).
  // Set true around any operation that would otherwise freeze the UI; the
  // <ProcessingOverlay/> below is rendered whenever this is true.
  const [isProcessingHoles, setIsProcessingHoles] = useState(false);
  // Mobile/tablet: by default the panel is "scroll-locked" (overflow-hidden)
  // so touch swipes don't accidentally scroll the page. A toggle next to the
  // Zurück/Weiter buttons lets the user unlock scrolling if compressed content
  // still overflows on very small screens. Desktop ignores this (always uses
  // its own scrollable sidebar).
  const [scrollUnlocked, setScrollUnlocked] = useState(false);
  // Touch-friendly value picker. When set, the bottom-sheet ValuePickerSheet
  // opens with the options. `onSelect` is called with the chosen value and
  // the picker closes. Used by EditorValueTile on mobile/tablet only.
  const [valuePicker, setValuePicker] = useState(null);
  const openPicker = useCallback((cfg) => setValuePicker(cfg), []);
  const closePicker = useCallback(() => setValuePicker(null), []);
  // Which phase of the hole-pattern work is currently visible in the overlay.
  //   'planning' → line preview being computed (OrthesisViewer's holePattern useMemo)
  //   'cutting'  → CSG hole subtraction running (cutShells effect, real progress 0–100)
  const [processingPhase, setProcessingPhase] = useState(null);
  const [processingProgress, setProcessingProgress] = useState(0);

  // Reset applied flag whenever the hole pattern parameters change. Changing
  // hole count / spacing means a fresh pattern → the user must click Next to
  // re-trigger the (slow) CSG cut. Otherwise the cached CSG result would still
  // be drawn while the live preview already shows the updated pattern.
  useEffect(() => {
    if (holePatternApplied) setHolePatternApplied(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holesPerRow, holeSpacing, holePatternEnabled]);

  // Design pattern selection drives hole-pattern generation. Initial value
  // is null = "user has not yet picked" → no preview is drawn, no CSG runs.
  // Selecting "none" explicitly = "no holes" (also disables the pattern).
  // Selecting "fdm" = enables the current FDM-optimised parallelogram/triangle design.
  const [designPattern, setDesignPattern] = useState(null);

  // Slider changes (holesPerRow, holeSpacing) re-run the line-preview useMemo
  // in OrthesisViewer too — show a brief overlay so the user has visual
  // feedback while the main thread is busy. Skip the very first render
  // (designPattern === null) and the no-holes mode.
  useEffect(() => {
    if (currentStep !== 5) return;
    if (!designPattern || designPattern === 'none') return;
    setProcessingPhase('planning');
    setProcessingProgress(0);
    setIsProcessingHoles(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holesPerRow, holeSpacing]);

  // Stable thumb point/normal — inline IIFEs in JSX would create a fresh
  // object every render, causing the planning useEffect in OrthesisViewer
  // (which depends on `thumbPoint`) to re-fire on every parent re-render and
  // restart the CSG cut. Memoize so identity is preserved across renders.
  const stableThumbPoint = useMemo(() => {
    if (!thumbBase || !thumbTip) return null;
    const t = thumbRingPos;
    return {
      x: thumbBase.x + (thumbTip.x - thumbBase.x) * t,
      y: thumbBase.y + (thumbTip.y - thumbBase.y) * t,
      z: thumbBase.z + (thumbTip.z - thumbBase.z) * t,
    };
  }, [thumbBase, thumbTip, thumbRingPos]);

  const stableThumbNormal = useMemo(() => {
    if (!thumbBase || !thumbTip) return null;
    const dx = thumbTip.x - thumbBase.x;
    const dy = thumbTip.y - thumbBase.y;
    const dz = thumbTip.z - thumbBase.z;
    const len = Math.sqrt(dx*dx + dy*dy + dz*dz);
    const n = len > 0.001 ? { x: dx/len, y: dy/len, z: dz/len } : { x: 0, y: 1, z: 0 };
    return adjustNormal(n, thumbTiltX, thumbTiltZ);
  }, [thumbBase, thumbTip, thumbTiltX, thumbTiltZ]);

  // Real planning progress from OrthesisViewer's async hole-pattern effect.
  // Reports 0–100; at 100 we clear the overlay. Slower on mobile, so a real
  // bar (instead of a fake ramp) is critical UX feedback.
  const handlePlanProgress = useCallback((progress) => {
    if (processingPhase !== 'planning') return;
    setProcessingProgress(progress);
    if (progress >= 100) {
      setTimeout(() => {
        setIsProcessingHoles(false);
        setProcessingPhase(null);
        setProcessingProgress(0);
      }, 200);
    }
  }, [processingPhase]);

  // CSG progress callback — wired into OrthesisViewer's per-hole cut effect.
  // Reports real 0–100 progress; when it hits 100 we clear the overlay and
  // advance to the export step.
  const handleCutProgress = useCallback((progress) => {
    if (processingPhase !== 'cutting') return;
    setProcessingProgress(progress);
    if (progress >= 100) {
      setTimeout(() => {
        setIsProcessingHoles(false);
        setProcessingPhase(null);
        setProcessingProgress(0);
        setCurrentStep(prev => prev === 5 ? 6 : prev);
      }, 300);
    }
  }, [processingPhase]);

  // Slit (opening) parameters
  const [slitRotation, setSlitRotation] = useState(Math.PI); // default: opposite thumb (180°)
  const [slitWidth, setSlitWidth] = useState(8); // mm
  const [slitTilt, setSlitTilt] = useState(0); // radians
  const [slitClickPoint, setSlitClickPoint] = useState(null);
  const [slitClickPoint2, setSlitClickPoint2] = useState(null);

  // Visibility toggles for Material & Design step
  const [hideRings, setHideRings] = useState(false);
  const [hideScan, setHideScan] = useState(false);

  const steps = [
    { id: "upload", label: t("orthesisEditor.stepUpload"), icon: CloudUpload },
    { id: "align", label: t("orthesisEditor.stepAlign"), icon: ArrowRight },
    { id: "fit", label: t("orthesisEditor.stepFit"), icon: Ruler },
    { id: "material", label: t("orthesisEditor.stepMaterial"), icon: Settings2 },
    { id: "opening", label: t("orthesisEditor.stepOpening") || "Öffnung", icon: SplitSquareHorizontal },
    { id: "pattern", label: "Lochmuster", icon: Settings2 },
    { id: "export", label: t("orthesisEditor.stepExport"), icon: Download },
  ];

  const handleFileChange = useCallback(async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setIsLoading(true);

    try {
      const zipFile = files.find((f) => f.name.endsWith(".zip"));
      const objFile = files.find((f) => f.name.endsWith(".obj"));
      const stlFile = files.find((f) => f.name.endsWith(".stl"));
      const pngFile = files.find((f) => f.name.endsWith(".png"));

      if (zipFile) {
        // Extract OBJ + texture from ZIP
        const zip = await JSZip.loadAsync(zipFile);
        const objInZip = Object.values(zip.files).find((f) => f.name.endsWith(".obj"));
        const stlInZip = Object.values(zip.files).find((f) => f.name.endsWith(".stl"));
        const mtlInZip = Object.values(zip.files).find((f) => f.name.endsWith(".mtl"));

        if (objInZip) {
          const objBlob = await objInZip.async("blob");
          setFile(new File([objBlob], objInZip.name));
          setFileType("obj");

          // Try to find texture via MTL
          if (mtlInZip) {
            const mtlText = await mtlInZip.async("text");
            const match = mtlText.match(/map_Kd\s+(.*\.png)/);
            if (match && match[1]) {
              const texFile = Object.values(zip.files).find((f) => f.name.endsWith(match[1]));
              if (texFile) {
                const blob = await texFile.async("blob");
                setTextureFile(new File([blob], texFile.name));
              }
            }
          } else {
            // Try any PNG in the zip
            const pngInZip = Object.values(zip.files).find((f) => f.name.endsWith(".png"));
            if (pngInZip) {
              const blob = await pngInZip.async("blob");
              setTextureFile(new File([blob], pngInZip.name));
            }
          }
        } else if (stlInZip) {
          const stlBlob = await stlInZip.async("blob");
          setFile(new File([stlBlob], stlInZip.name));
          setFileType("stl");
        } else {
          showNotification(t("orthesisEditor.unsupportedFormat"), "error");
          setIsLoading(false);
          return;
        }
      } else if (objFile) {
        setFile(objFile);
        setFileType("obj");
        if (pngFile) setTextureFile(pngFile);
      } else if (stlFile) {
        setFile(stlFile);
        setFileType("stl");
      } else {
        showNotification(t("orthesisEditor.unsupportedFormat"), "error");
        setIsLoading(false);
        return;
      }

      setCurrentStep(1);
      showNotification(t("orthesisEditor.fileLoaded"), "success");
    } catch (err) {
      console.error("File loading error:", err);
      showNotification(t("orthesisEditor.unsupportedFormat"), "error");
    } finally {
      setIsLoading(false);
    }
  }, [showNotification, t]);

  const handleAlignClick = useCallback((mode, point) => {
    const runAlignment = (p1, p2) => {
      if (!viewerRef.current) return;
      const rotated = viewerRef.current.alignModel(p1, p2);
      if (rotated) {
        setAlignPoint1(rotated.p1);
        setAlignPoint2(rotated.p2);
        // Mirror rotated points into the ref so handleModelLoaded can
        // use them for smart cut placement on the next render tick.
        alignDataRef.current = { p1: rotated.p1, p2: rotated.p2 };
      }
      setIsAligned(true);
      showNotification(t("orthesisEditor.alignComplete"), "success");
    };

    if (mode === 'align1') {
      setAlignPoint1(point);
      setClickMode(null);
      setIsAligned(false);
      showNotification(t("orthesisEditor.alignPoint1Set"), "success");
      if (alignPoint2) runAlignment(point, alignPoint2);
    } else if (mode === 'align2') {
      setAlignPoint2(point);
      setClickMode(null);
      setIsAligned(false);
      showNotification(t("orthesisEditor.alignPoint2Set"), "success");
      if (alignPoint1) runAlignment(alignPoint1, point);
    }
  }, [showNotification, t, alignPoint1, alignPoint2]);

  const handleModelLoaded = useCallback((bounds) => {
    setModelBounds(bounds);
    const range = bounds.maxY - bounds.minY;

    // Smart placement once the model is aligned: the rotated wrist
    // position (alignDataRef.current.p1) gives us the boundary between
    // forearm and hand. The top of the thumb ring (= where the orthesis
    // upper edge should sit) is roughly 55% of the way from the wrist
    // to the fingertips for a typical hand → upperCutY = wrist + 55% of
    // (maxY − wristY).
    const wrist = alignDataRef.current.p1;
    if (wrist && typeof wrist.y === 'number') {
      const handRange = bounds.maxY - wrist.y;
      if (handRange > 1) {
        setUpperCutY(wrist.y + handRange * 0.50);
        setLowerCutY(bounds.minY + range * 0.15);
        return;
      }
    }
    // Fallback for the very first load (before alignment): use a coarse
    // percentage of the full Y range. 0.55 mirrors the post-alignment
    // value approximately when the model is hand-only.
    setUpperCutY(bounds.minY + range * 0.50);
    setLowerCutY(bounds.minY + range * 0.15);
  }, []);

  const handleRingClick = useCallback((mode, clickPoint, normal, centerPoint) => {
    setClickMode(null);
    if (mode === 'upper') {
      setUpperCutY(clickPoint.y);
      setUpperClickPoint(clickPoint);
      setUpperTiltX(0);
      setUpperTiltZ(0);
      // Ring goes through center at click Y, not through click point on surface
      setUpperPoint(centerPoint || clickPoint);
      setUpperNormal(normal);
      showNotification(t("orthesisEditor.ringPlaced"), "success");
    } else if (mode === 'lower') {
      setLowerCutY(clickPoint.y);
      setLowerClickPoint(clickPoint);
      setLowerTiltX(0);
      setLowerTiltZ(0);
      setLowerPoint(centerPoint || clickPoint);
      setLowerNormal(normal);
      showNotification(t("orthesisEditor.ringPlaced"), "success");
    } else if (mode === 'thumbBase') {
      setThumbBase(clickPoint);
      if (thumbTip) {
        setThumbRingPos(thumbMode === 'free' ? 0.15 : 0.5);
      }
      showNotification(thumbTip ? t("orthesisEditor.thumbAxisSet") : t("orthesisEditor.thumbBaseSet"), "success");
    } else if (mode === 'thumbTip') {
      setThumbTip(clickPoint);
      if (thumbBase) {
        setThumbRingPos(thumbMode === 'free' ? 0.15 : 0.5);
      }
      showNotification(thumbBase ? t("orthesisEditor.thumbAxisSet") : t("orthesisEditor.thumbTipSet"), "success");
    } else if (mode === 'thumbRing') {
      // Project click point onto thumb axis to get position parameter
      if (thumbBase && thumbTip) {
        const ax = thumbTip.x - thumbBase.x, ay = thumbTip.y - thumbBase.y, az = thumbTip.z - thumbBase.z;
        const aLen = Math.sqrt(ax*ax + ay*ay + az*az);
        if (aLen > 0.001) {
          const dx = clickPoint.x - thumbBase.x, dy = clickPoint.y - thumbBase.y, dz = clickPoint.z - thumbBase.z;
          const param = (dx*ax + dy*ay + dz*az) / (aLen*aLen);
          setThumbRingPos(Math.max(-1.2, Math.min(1.2, param)));
          setThumbEnabled(true);
          showNotification(t("orthesisEditor.ringPlaced"), "success");
        }
      }
    } else if (mode === 'slitPlace') {
      setSlitClickPoint(clickPoint);
      showNotification(t("orthesisEditor.slitPlaced") || "Schlitzposition gesetzt", "success");
    } else if (mode === 'slitPlace2') {
      setSlitClickPoint2(clickPoint);
      showNotification(t("orthesisEditor.slitTiltSet") || "Neigung gesetzt", "success");
    }
  }, [showNotification, t, thumbBase, thumbTip, thumbMode]);

  const handleExportJson = useCallback(() => {
    const params = {
      version: "1.0",
      timestamp: new Date().toISOString(),
      sourceFile: file?.name,
      parameters: {
        fit: {
          upperCutY: upperCutY || 0,
          lowerCutY: lowerCutY || 0,
          upperTilt: { x: upperTiltX, z: upperTiltZ },
          lowerTilt: { x: lowerTiltX, z: lowerTiltZ },
          thumbOpening: {
            enabled: thumbEnabled,
            mode: thumbMode,
            base: thumbBase ? { x: thumbBase.x, y: thumbBase.y, z: thumbBase.z } : null,
            tip: thumbTip ? { x: thumbTip.x, y: thumbTip.y, z: thumbTip.z } : null,
            ringPosition: thumbRingPos,
          },
        },
        material: {
          wallThickness,
          skinOffset,
        },
        design: {
          closureCount,
          pattern: designPattern,
        },
      },
    };
    const blob = new Blob([JSON.stringify(params, null, 2)], { type: "application/json" });
    saveAs(blob, `orthesis-config-${Date.now()}.json`);
    showNotification(t("orthesisEditor.exportSuccess"), "success");
  }, [file, upperCutY, lowerCutY, upperTiltX, upperTiltZ, lowerTiltX, lowerTiltZ, thumbEnabled, thumbBase, thumbTip, thumbMode, thumbRingPos, wallThickness, skinOffset, closureCount, designPattern, showNotification, t]);

  const handleExportOrthesisStl = useCallback(async () => {
    const shellGeos = viewerRef.current?.getShellGeometries();
    if (!shellGeos) {
      showNotification("Keine Orthesen-Geometrie vorhanden", "error");
      return;
    }

    const { Group, Mesh: ThreeMesh, MeshBasicMaterial, BufferGeometry,
            Float32BufferAttribute, Vector3: Vec3, Plane: P3 } = await import('three');

    // Clip a geometry against planes: split triangles at plane boundary
    function clipGeometry(geo, planes) {
      if (!planes || planes.length === 0) return geo;
      const posAttr = geo.attributes.position;
      const idx = geo.index;

      // Collect current triangles
      const tris = [];
      const triCount = idx ? idx.count / 3 : posAttr.count / 3;
      for (let t = 0; t < triCount; t++) {
        const a = idx ? idx.getX(t*3) : t*3;
        const b = idx ? idx.getX(t*3+1) : t*3+1;
        const c = idx ? idx.getX(t*3+2) : t*3+2;
        tris.push([
          new Vec3(posAttr.getX(a), posAttr.getY(a), posAttr.getZ(a)),
          new Vec3(posAttr.getX(b), posAttr.getY(b), posAttr.getZ(b)),
          new Vec3(posAttr.getX(c), posAttr.getY(c), posAttr.getZ(c)),
        ]);
      }

      // Clip each triangle against each plane
      let currentTris = tris;
      for (const plane of planes) {
        const nextTris = [];
        for (const tri of currentTris) {
          const dists = tri.map(v => plane.distanceToPoint(v));
          const inside = dists.map(d => d >= -0.001);
          const numInside = inside.filter(Boolean).length;

          if (numInside === 3) {
            nextTris.push(tri); // fully inside
          } else if (numInside === 0) {
            continue; // fully outside
          } else {
            // Partially inside — split triangle
            const vIn = [], vOut = [];
            for (let i = 0; i < 3; i++) {
              if (inside[i]) vIn.push(i); else vOut.push(i);
            }

            if (numInside === 1) {
              // 1 inside, 2 outside → small triangle
              const a = vIn[0], b = vOut[0], c = vOut[1];
              const ab = new Vec3().lerpVectors(tri[a], tri[b], dists[a] / (dists[a] - dists[b]));
              const ac = new Vec3().lerpVectors(tri[a], tri[c], dists[a] / (dists[a] - dists[c]));
              nextTris.push([tri[a].clone(), ab, ac]);
            } else {
              // 2 inside, 1 outside → quad (2 triangles)
              const a = vOut[0], b = vIn[0], c = vIn[1];
              const ab = new Vec3().lerpVectors(tri[a], tri[b], dists[a] / (dists[a] - dists[b]));
              const ac = new Vec3().lerpVectors(tri[a], tri[c], dists[a] / (dists[a] - dists[c]));
              nextTris.push([tri[b].clone(), tri[c].clone(), ab]);
              nextTris.push([ab.clone(), tri[c].clone(), ac]);
            }
          }
        }
        currentTris = nextTris;
      }

      // Build geometry from clipped triangles
      const outPos = [];
      const outIdx = [];
      let vCount = 0;
      for (const tri of currentTris) {
        for (const v of tri) outPos.push(v.x, v.y, v.z);
        outIdx.push(vCount, vCount+1, vCount+2);
        vCount += 3;
      }

      if (outPos.length < 9) return null;
      const g = new BufferGeometry();
      g.setAttribute('position', new Float32BufferAttribute(new Float32Array(outPos), 3));
      g.setIndex(outIdx);
      g.computeVertexNormals();
      return g;
    }

    const exporter = new STLExporter();
    const shellGroup = new Group();
    const mat = new MeshBasicMaterial();
    const planes = shellGeos.clippingPlanes || [];

    // Use split data if available (arm/thumb regions with separate clipping)
    // This matches the preview exactly: arm gets upper+lower, thumb gets lower+thumb
    const hasSplit = shellGeos.armOuter || shellGeos.thumbOuter;
    let clippedOuter, clippedInner;

    if (hasSplit) {
      const armPlanes = shellGeos.armClipPlanes || [];
      const thumbPlanes = shellGeos.thumbClipPlanes || [];

      // Clip each region with its own planes, then merge
      const clipAndMerge = (armGeo, thumbGeo) => {
        const parts = [];
        if (armGeo) {
          const c = clipGeometry(armGeo, armPlanes);
          if (c) parts.push(c);
        }
        if (thumbGeo) {
          const c = clipGeometry(thumbGeo, thumbPlanes);
          if (c) parts.push(c);
        }
        if (parts.length === 0) return null;
        if (parts.length === 1) return parts[0];
        // Merge multiple geometries
        const allPos = [], allIdx = [];
        let offset = 0;
        for (const g of parts) {
          const pos = g.attributes.position;
          const idx = g.index;
          for (let i = 0; i < pos.count; i++) allPos.push(pos.getX(i), pos.getY(i), pos.getZ(i));
          if (idx) {
            for (let i = 0; i < idx.count; i++) allIdx.push(idx.getX(i) + offset);
          } else {
            for (let i = 0; i < pos.count; i++) allIdx.push(i + offset);
          }
          offset += pos.count;
        }
        const merged = new BufferGeometry();
        merged.setAttribute('position', new Float32BufferAttribute(new Float32Array(allPos), 3));
        merged.setIndex(allIdx);
        merged.computeVertexNormals();
        return merged;
      };

      clippedOuter = clipAndMerge(shellGeos.armOuter, shellGeos.thumbOuter);
      clippedInner = clipAndMerge(shellGeos.armInner, shellGeos.thumbInner);
    } else {
      // Fallback: clip everything with all planes (no thumb split)
      clippedOuter = shellGeos.outerGeo ? clipGeometry(shellGeos.outerGeo, planes) : null;
      clippedInner = shellGeos.innerGeo ? clipGeometry(shellGeos.innerGeo, planes) : null;
    }

    // ============================================================
    // Step 0.5: Apply slit (plane-clip + back-side preservation)
    // Same method as preview: applySlit in OrthesisViewer.jsx
    // ============================================================
    if (shellGeos.slitPlanes && shellGeos.slitPlanes.length === 2) {
      const sp = shellGeos.slitPlanes;
      const axDir = shellGeos.slitAxis ? shellGeos.slitAxis.dir : new Vec3(0, 1, 0);
      const ctr = shellGeos.slitAxis ? shellGeos.slitAxis.center : new Vec3();
      let radA = null;
      if (shellGeos.slitClickPoint) {
        radA = new Vec3().subVectors(shellGeos.slitClickPoint, ctr);
        radA.sub(axDir.clone().multiplyScalar(radA.dot(axDir)));
        if (radA.length() > 0.01) radA.normalize(); else radA = null;
      }

      // Clip triangle array with a single plane
      const clipTris = (tris, plane) => {
        const next = [];
        for (const tri of tris) {
          const dists = tri.map(v => plane.distanceToPoint(v));
          const inside = dists.map(d => d >= -0.001);
          const numIn = inside.filter(Boolean).length;
          if (numIn === 3) { next.push(tri); continue; }
          if (numIn === 0) { continue; }
          const vIn = [], vOut = [];
          for (let i = 0; i < 3; i++) { if (inside[i]) vIn.push(i); else vOut.push(i); }
          if (numIn === 1) {
            const a=vIn[0], b=vOut[0], c=vOut[1];
            next.push([tri[a].clone(), new Vec3().lerpVectors(tri[a],tri[b],dists[a]/(dists[a]-dists[b])), new Vec3().lerpVectors(tri[a],tri[c],dists[a]/(dists[a]-dists[c]))]);
          } else {
            const a=vOut[0], b=vIn[0], c=vIn[1];
            const ab = new Vec3().lerpVectors(tri[a],tri[b],dists[a]/(dists[a]-dists[b]));
            const ac = new Vec3().lerpVectors(tri[a],tri[c],dists[a]/(dists[a]-dists[c]));
            next.push([tri[b].clone(), tri[c].clone(), ab]);
            next.push([ab.clone(), tri[c].clone(), ac]);
          }
        }
        return next;
      };

      const applySlitToGeo = (geo) => {
        if (!geo) return geo;
        const pos = geo.attributes.position, idx = geo.index;
        if (!pos || !idx) return geo;

        // Split into A-side and back-side using separator plane through axis
        const allTrisRaw = [];
        const tc = idx.count / 3;
        for (let t=0; t<tc; t++) {
          const a=idx.getX(t*3), b2=idx.getX(t*3+1), c=idx.getX(t*3+2);
          allTrisRaw.push([new Vec3(pos.getX(a),pos.getY(a),pos.getZ(a)), new Vec3(pos.getX(b2),pos.getY(b2),pos.getZ(b2)), new Vec3(pos.getX(c),pos.getY(c),pos.getZ(c))]);
        }
        let aSide, backSide;
        if (radA) {
          const sepP = new P3(radA.clone(), -radA.dot(ctr));
          const sepPInv = new P3(radA.clone().negate(), radA.dot(ctr));
          aSide = clipTris(allTrisRaw, sepP);
          backSide = clipTris(allTrisRaw, sepPInv);
        } else {
          aSide = allTrisRaw;
          backSide = [];
        }

        // Clip A-side with each plane separately, merge results
        const leftHalf = clipTris(aSide, sp[0]);
        const rightHalf = clipTris(aSide, sp[1]);
        const clipped = [...leftHalf, ...rightHalf];

        // Merge clipped A-side + untouched back-side
        const all = [...clipped, ...backSide];
        if (all.length < 1) return null;
        const op=[], oi=[]; let ov=0;
        for (const tri of all) { for (const v of tri) op.push(v.x,v.y,v.z); oi.push(ov,ov+1,ov+2); ov+=3; }
        const g = new BufferGeometry();
        g.setAttribute('position', new Float32BufferAttribute(new Float32Array(op), 3));
        g.setIndex(oi); g.computeVertexNormals();
        return g;
      };

      clippedOuter = applySlitToGeo(clippedOuter);
      clippedInner = applySlitToGeo(clippedInner);
    }

    // ============================================================
    // Step 1: Fix winding on clipped shells BEFORE building caps
    // ============================================================
    function fixWinding(geo) {
      if (!geo) return geo;
      const pos = geo.attributes.position;
      const idx = geo.index;
      if (!pos || !idx) return geo;

      const PREC = 100;
      const vk2 = (i) => `${Math.round(pos.getX(i)*PREC)}_${Math.round(pos.getY(i)*PREC)}_${Math.round(pos.getZ(i)*PREC)}`;

      // Weld
      const keyToNew = new Map();
      const oldToNew = new Int32Array(pos.count);
      const wPos = [];
      let wCount = 0;
      for (let i = 0; i < pos.count; i++) {
        const k = vk2(i);
        if (keyToNew.has(k)) { oldToNew[i] = keyToNew.get(k); }
        else { keyToNew.set(k, wCount); oldToNew[i] = wCount; wPos.push(pos.getX(i), pos.getY(i), pos.getZ(i)); wCount++; }
      }

      const faces2 = [];
      for (let i = 0; i < idx.count; i += 3) {
        const a = oldToNew[idx.getX(i)], b = oldToNew[idx.getX(i+1)], c = oldToNew[idx.getX(i+2)];
        if (a !== b && b !== c && a !== c) faces2.push([a, b, c]);
      }

      // BFS winding fix
      const ek2 = (a, b) => a < b ? `${a}_${b}` : `${b}_${a}`;
      const edgeToFaces2 = new Map();
      for (let fi = 0; fi < faces2.length; fi++) {
        const [a, b, c] = faces2[fi];
        for (const [e0, e1] of [[a,b],[b,c],[c,a]]) {
          const k = ek2(e0, e1);
          if (!edgeToFaces2.has(k)) edgeToFaces2.set(k, []);
          edgeToFaces2.get(k).push(fi);
        }
      }
      const vis = new Uint8Array(faces2.length);
      for (let start = 0; start < faces2.length; start++) {
        if (vis[start]) continue;
        const q = [start]; vis[start] = 1;
        while (q.length > 0) {
          const fi = q.shift();
          const [fa, fb, fc] = faces2[fi];
          for (const [e0, e1] of [[fa,fb],[fb,fc],[fc,fa]]) {
            const nbrs = edgeToFaces2.get(ek2(e0, e1));
            if (!nbrs) continue;
            for (const ni of nbrs) {
              if (vis[ni]) continue;
              vis[ni] = 1;
              const [na, nb, nc] = faces2[ni];
              for (const [ne0, ne1] of [[na,nb],[nb,nc],[nc,na]]) {
                if (ek2(ne0, ne1) === ek2(e0, e1)) {
                  if (ne0 === e0 && ne1 === e1) faces2[ni] = [na, nc, nb];
                  break;
                }
              }
              q.push(ni);
            }
          }
        }
      }

      // Outward normal check
      let cx2 = 0, cy2 = 0, cz2 = 0;
      for (let i = 0; i < wCount; i++) { cx2 += wPos[i*3]; cy2 += wPos[i*3+1]; cz2 += wPos[i*3+2]; }
      cx2 /= wCount; cy2 /= wCount; cz2 /= wCount;
      let outCnt = 0;
      const step2 = Math.max(1, Math.floor(faces2.length / 200));
      for (let fi = 0; fi < faces2.length; fi += step2) {
        const [a, b, c] = faces2[fi];
        const e1x = wPos[b*3]-wPos[a*3], e1y = wPos[b*3+1]-wPos[a*3+1], e1z = wPos[b*3+2]-wPos[a*3+2];
        const e2x = wPos[c*3]-wPos[a*3], e2y = wPos[c*3+1]-wPos[a*3+1], e2z = wPos[c*3+2]-wPos[a*3+2];
        const fnx = e1y*e2z-e1z*e2y, fny = e1z*e2x-e1x*e2z, fnz = e1x*e2y-e1y*e2x;
        const dx = (wPos[a*3]+wPos[b*3]+wPos[c*3])/3-cx2;
        const dy = (wPos[a*3+1]+wPos[b*3+1]+wPos[c*3+1])/3-cy2;
        const dz = (wPos[a*3+2]+wPos[b*3+2]+wPos[c*3+2])/3-cz2;
        if (fnx*dx + fny*dy + fnz*dz > 0) outCnt++;
      }
      if (outCnt < faces2.length / step2 / 2) {
        for (const f of faces2) { const tmp = f[1]; f[1] = f[2]; f[2] = tmp; }
      }

      const g = new BufferGeometry();
      g.setAttribute('position', new Float32BufferAttribute(new Float32Array(wPos), 3));
      const idxArr = [];
      for (const [a, b, c] of faces2) idxArr.push(a, b, c);
      g.setIndex(idxArr);
      g.computeVertexNormals();
      g.computeBoundingSphere();
      return g;
    }

    const fixedOuter = fixWinding(clippedOuter);
    const fixedInner = fixWinding(clippedInner);

    if (fixedOuter) shellGroup.add(new ThreeMesh(fixedOuter, mat));
    if (fixedInner) shellGroup.add(new ThreeMesh(fixedInner, mat));

    // Add closure (velcro strap) geometries
    if (shellGeos.closureGeos && shellGeos.closureGeos.length > 0) {
      for (const cg of shellGeos.closureGeos) {
        shellGroup.add(new ThreeMesh(cg, mat));
      }
    }

    // ============================================================
    // Step 2: Build caps from boundary edges of the FIXED shells
    // ============================================================
    if (fixedOuter && fixedInner) {
      const PREC = 100;
      const vk3 = (x, y, z) => `${Math.round(x*PREC)}_${Math.round(y*PREC)}_${Math.round(z*PREC)}`;

      const findBoundaryEdgesNearPlanes = (geo) => {
        const pos = geo.attributes.position;
        const idx = geo.index;
        if (!pos || !idx) return [];
        const edgeCount = new Map();
        const edgeData = new Map();
        for (let i = 0; i < idx.count; i += 3) {
          const vis = [];
          for (let k = 0; k < 3; k++) {
            const vi = idx.getX(i + k);
            vis.push({ idx: vi, pos: new Vec3(pos.getX(vi), pos.getY(vi), pos.getZ(vi)) });
          }
          for (const [a, b] of [[0,1],[1,2],[2,0]]) {
            const ka = vk3(vis[a].pos.x, vis[a].pos.y, vis[a].pos.z);
            const kb = vk3(vis[b].pos.x, vis[b].pos.y, vis[b].pos.z);
            const ek3 = ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
            edgeCount.set(ek3, (edgeCount.get(ek3) || 0) + 1);
            if (!edgeData.has(ek3)) edgeData.set(ek3, [vis[a].pos, vis[b].pos]);
          }
        }
        const boundary = [];
        const allCapPlanes = [...planes, ...(shellGeos.slitPlanes || [])];
        for (const [ek3, count] of edgeCount) {
          if (count === 1) {
            const [a, b] = edgeData.get(ek3);
            const mid = new Vec3().addVectors(a, b).multiplyScalar(0.5);
            let nearPlane = false;
            for (const plane of allCapPlanes) {
              if (Math.abs(plane.distanceToPoint(mid)) < 2.0) { nearPlane = true; break; }
            }
            if (nearPlane) boundary.push([a, b]);
          }
        }
        return boundary;
      };

      const outerBE = findBoundaryEdgesNearPlanes(fixedOuter);
      const innerBE = findBoundaryEdgesNearPlanes(fixedInner);

      // Match each outer edge to its nearest inner edge
      if (outerBE.length > 0 && innerBE.length > 0) {
        const capPos = [];
        const capIdx = [];
        let capVC = 0;

        for (const [oA, oB] of outerBE) {
          const oMid = new Vec3().addVectors(oA, oB).multiplyScalar(0.5);

          // Find nearest inner edge by midpoint distance
          let bestDist = Infinity, bestInner = null;
          for (const [iA, iB] of innerBE) {
            const d = oMid.distanceTo(new Vec3().addVectors(iA, iB).multiplyScalar(0.5));
            if (d < bestDist) { bestDist = d; bestInner = [iA, iB]; }
          }
          if (!bestInner || bestDist > 15) continue;

          // Determine correct vertex pairing: oA↔iA or oA↔iB
          const d1 = oA.distanceTo(bestInner[0]) + oB.distanceTo(bestInner[1]);
          const d2 = oA.distanceTo(bestInner[1]) + oB.distanceTo(bestInner[0]);
          const [iA, iB] = d1 <= d2 ? bestInner : [bestInner[1], bestInner[0]];

          const base = capVC;
          capPos.push(oA.x, oA.y, oA.z);   // 0
          capPos.push(oB.x, oB.y, oB.z);   // 1
          capPos.push(iA.x, iA.y, iA.z);   // 2
          capPos.push(iB.x, iB.y, iB.z);   // 3
          capIdx.push(base, base+1, base+2);
          capIdx.push(base+2, base+1, base+3);
          capVC += 4;
        }

        if (capPos.length >= 12) {
          const capGeo = new BufferGeometry();
          capGeo.setAttribute('position', new Float32BufferAttribute(new Float32Array(capPos), 3));
          capGeo.setIndex(capIdx);
          capGeo.computeVertexNormals();
          shellGroup.add(new ThreeMesh(capGeo, mat));
        }
      }
    }

    // ============================================================
    // Step 3: Fill remaining small holes (boundary edges not covered by caps)
    // ============================================================
    {
      // Merge all meshes in shellGroup into one geo for hole analysis
      const mergedPos = [];
      const mergedIdx = [];
      let mOffset = 0;
      shellGroup.traverse((child) => {
        if (!child.isMesh || !child.geometry) return;
        const pos = child.geometry.attributes.position;
        const idx = child.geometry.index;
        if (!pos) return;
        for (let i = 0; i < pos.count; i++) mergedPos.push(pos.getX(i), pos.getY(i), pos.getZ(i));
        if (idx) {
          for (let i = 0; i < idx.count; i++) mergedIdx.push(idx.getX(i) + mOffset);
        } else {
          for (let i = 0; i < pos.count; i++) mergedIdx.push(i + mOffset);
        }
        mOffset += pos.count;
      });

      // Weld vertices for correct topology
      const PREC2 = 100;
      const wKey = (i) => `${Math.round(mergedPos[i*3]*PREC2)}_${Math.round(mergedPos[i*3+1]*PREC2)}_${Math.round(mergedPos[i*3+2]*PREC2)}`;
      const wMap = new Map();
      const wOldToNew = new Int32Array(mOffset);
      const wPos2 = [];
      let wCnt = 0;
      for (let i = 0; i < mOffset; i++) {
        const k = wKey(i);
        if (wMap.has(k)) { wOldToNew[i] = wMap.get(k); }
        else { wMap.set(k, wCnt); wOldToNew[i] = wCnt; wPos2.push(mergedPos[i*3], mergedPos[i*3+1], mergedPos[i*3+2]); wCnt++; }
      }
      const wIdx = mergedIdx.map(i => wOldToNew[i]);

      // Find boundary edges (used by exactly 1 triangle)
      const edgeCnt = new Map();
      const edgeVerts = new Map();
      for (let i = 0; i < wIdx.length; i += 3) {
        const a = wIdx[i], b = wIdx[i+1], c = wIdx[i+2];
        for (const [e0, e1] of [[a,b],[b,c],[c,a]]) {
          const ek = e0 < e1 ? `${e0}_${e1}` : `${e1}_${e0}`;
          edgeCnt.set(ek, (edgeCnt.get(ek) || 0) + 1);
          if (!edgeVerts.has(ek)) edgeVerts.set(ek, [e0, e1]);
        }
      }

      // Collect boundary edges
      const boundaryEdges = [];
      for (const [ek, cnt] of edgeCnt) {
        if (cnt === 1) boundaryEdges.push(edgeVerts.get(ek));
      }

      if (boundaryEdges.length > 0) {
        // Build adjacency: vertex → connected boundary vertices
        const adj = new Map();
        for (const [a, b] of boundaryEdges) {
          if (!adj.has(a)) adj.set(a, []);
          if (!adj.has(b)) adj.set(b, []);
          adj.get(a).push(b);
          adj.get(b).push(a);
        }

        // Trace boundary loops
        const visited = new Set();
        const loops = [];
        for (const startV of adj.keys()) {
          if (visited.has(startV)) continue;
          const loop = [];
          let curr = startV;
          let prev = -1;
          while (!visited.has(curr)) {
            visited.add(curr);
            loop.push(curr);
            const neighbors = adj.get(curr);
            if (!neighbors) break;
            const next = neighbors.find(n => n !== prev) ?? neighbors[0];
            if (visited.has(next) && next === startV && loop.length >= 3) break;
            if (visited.has(next)) break;
            prev = curr;
            curr = next;
          }
          if (loop.length >= 3) loops.push(loop);
        }

        // Fill each small loop with a triangle fan
        const fillPos = [];
        const fillIdx = [];
        let fillVC = 0;

        for (const loop of loops) {
          if (loop.length > 50) continue; // skip large loops (not holes)

          // Compute centroid
          let cx = 0, cy = 0, cz = 0;
          for (const vi of loop) {
            cx += wPos2[vi*3]; cy += wPos2[vi*3+1]; cz += wPos2[vi*3+2];
          }
          cx /= loop.length; cy /= loop.length; cz /= loop.length;

          // Add centroid vertex
          const centerIdx = fillVC;
          fillPos.push(cx, cy, cz);
          fillVC++;

          // Add loop vertices
          const loopBase = fillVC;
          for (const vi of loop) {
            fillPos.push(wPos2[vi*3], wPos2[vi*3+1], wPos2[vi*3+2]);
            fillVC++;
          }

          // Fan triangles from centroid
          for (let i = 0; i < loop.length; i++) {
            const next = (i + 1) % loop.length;
            fillIdx.push(centerIdx, loopBase + i, loopBase + next);
          }

          // Check normal orientation: compare fill normal against neighboring mesh normal
          // Use the first boundary edge's adjacent triangle to determine correct outward direction
          const firstV = loop[0], secondV = loop[1];
          // Find the existing triangle that uses this edge
          let refNormal = null;
          for (let i = 0; i < wIdx.length; i += 3) {
            const ta = wIdx[i], tb = wIdx[i+1], tc = wIdx[i+2];
            const has1 = (ta === firstV || tb === firstV || tc === firstV);
            const has2 = (ta === secondV || tb === secondV || tc === secondV);
            if (has1 && has2) {
              const ax = wPos2[ta*3], ay = wPos2[ta*3+1], az = wPos2[ta*3+2];
              const bx = wPos2[tb*3], by = wPos2[tb*3+1], bz = wPos2[tb*3+2];
              const ccx = wPos2[tc*3], ccy = wPos2[tc*3+1], ccz = wPos2[tc*3+2];
              const e1x = bx-ax, e1y = by-ay, e1z = bz-az;
              const e2x = ccx-ax, e2y = ccy-ay, e2z = ccz-az;
              refNormal = { x: e1y*e2z-e1z*e2y, y: e1z*e2x-e1x*e2z, z: e1x*e2y-e1y*e2x };
              break;
            }
          }
          if (refNormal) {
            // Compute normal of first fill triangle
            const fi = fillIdx.length - loop.length * 3; // start of this fan's indices
            const ia = fillIdx[fi], ib = fillIdx[fi+1], ic = fillIdx[fi+2];
            const ax2 = fillPos[ia*3], ay2 = fillPos[ia*3+1], az2 = fillPos[ia*3+2];
            const bx2 = fillPos[ib*3], by2 = fillPos[ib*3+1], bz2 = fillPos[ib*3+2];
            const cx2 = fillPos[ic*3], cy2 = fillPos[ic*3+1], cz2 = fillPos[ic*3+2];
            const fe1x = bx2-ax2, fe1y = by2-ay2, fe1z = bz2-az2;
            const fe2x = cx2-ax2, fe2y = cy2-ay2, fe2z = cz2-az2;
            const fillN = { x: fe1y*fe2z-fe1z*fe2y, y: fe1z*fe2x-fe1x*fe2z, z: fe1x*fe2y-fe1y*fe2x };
            // If normals point in opposite directions, flip all fan triangles
            const dot = fillN.x*refNormal.x + fillN.y*refNormal.y + fillN.z*refNormal.z;
            if (dot < 0) {
              for (let i = fillIdx.length - loop.length * 3; i < fillIdx.length; i += 3) {
                const tmp = fillIdx[i+1];
                fillIdx[i+1] = fillIdx[i+2];
                fillIdx[i+2] = tmp;
              }
            }
          }
        }

        if (fillPos.length >= 9) {
          const fillGeo = new BufferGeometry();
          fillGeo.setAttribute('position', new Float32BufferAttribute(new Float32Array(fillPos), 3));
          fillGeo.setIndex(fillIdx);
          fillGeo.computeVertexNormals();
          shellGroup.add(new ThreeMesh(fillGeo, mat));
        }
      }
    }

    // ============================================================
    // Step 4: Final mesh repair — merge all into one manifold mesh
    // ============================================================
    {
      // Collect all triangles from all meshes in shellGroup
      const allPos2 = [];
      const allFaces2 = [];
      let vOff = 0;
      shellGroup.traverse((child) => {
        if (!child.isMesh || !child.geometry) return;
        const pos = child.geometry.attributes.position;
        const idx = child.geometry.index;
        if (!pos) return;
        for (let i = 0; i < pos.count; i++) allPos2.push(pos.getX(i), pos.getY(i), pos.getZ(i));
        const tc = idx ? idx.count / 3 : pos.count / 3;
        for (let t = 0; t < tc; t++) {
          const a = (idx ? idx.getX(t*3) : t*3) + vOff;
          const b = (idx ? idx.getX(t*3+1) : t*3+1) + vOff;
          const c = (idx ? idx.getX(t*3+2) : t*3+2) + vOff;
          allFaces2.push(a, b, c);
        }
        vOff += pos.count;
      });

      const totalVerts = allPos2.length / 3;
      if (totalVerts >= 3 && allFaces2.length >= 3) {
        // Weld vertices
        const WP = 200; // precision for welding
        const wk = (i) => `${Math.round(allPos2[i*3]*WP)}_${Math.round(allPos2[i*3+1]*WP)}_${Math.round(allPos2[i*3+2]*WP)}`;
        const wm = new Map();
        const o2n = new Int32Array(totalVerts);
        const weldPos = [];
        let wc = 0;
        for (let i = 0; i < totalVerts; i++) {
          const k = wk(i);
          if (wm.has(k)) { o2n[i] = wm.get(k); }
          else { wm.set(k, wc); o2n[i] = wc; weldPos.push(allPos2[i*3], allPos2[i*3+1], allPos2[i*3+2]); wc++; }
        }

        // Remap faces, remove degenerate
        const faces = [];
        const faceSet = new Set();
        for (let i = 0; i < allFaces2.length; i += 3) {
          const a = o2n[allFaces2[i]], b = o2n[allFaces2[i+1]], c = o2n[allFaces2[i+2]];
          if (a === b || b === c || a === c) continue; // degenerate
          // Deduplicate (canonical key)
          const sorted = [a,b,c].sort((x,y) => x-y);
          const fk = `${sorted[0]}_${sorted[1]}_${sorted[2]}`;
          if (faceSet.has(fk)) continue;
          faceSet.add(fk);
          faces.push(a, b, c);
        }

        // BFS winding consistency
        const nf = faces.length / 3;
        const ek3 = (a, b) => a < b ? `${a}_${b}` : `${b}_${a}`;
        const e2f = new Map();
        for (let fi = 0; fi < nf; fi++) {
          const j = fi * 3;
          for (const [e0, e1] of [[faces[j],faces[j+1]],[faces[j+1],faces[j+2]],[faces[j+2],faces[j]]]) {
            const k = ek3(e0, e1);
            if (!e2f.has(k)) e2f.set(k, []);
            e2f.get(k).push(fi);
          }
        }
        const vis2 = new Uint8Array(nf);
        for (let start = 0; start < nf; start++) {
          if (vis2[start]) continue;
          const q = [start]; vis2[start] = 1;
          while (q.length > 0) {
            const fi = q.shift();
            const fj = fi * 3;
            for (const [e0, e1] of [[faces[fj],faces[fj+1]],[faces[fj+1],faces[fj+2]],[faces[fj+2],faces[fj]]]) {
              const nbrs = e2f.get(ek3(e0, e1));
              if (!nbrs) continue;
              for (const ni of nbrs) {
                if (vis2[ni]) continue;
                vis2[ni] = 1;
                const nj = ni * 3;
                const na = faces[nj], nb = faces[nj+1], nc = faces[nj+2];
                for (const [ne0, ne1] of [[na,nb],[nb,nc],[nc,na]]) {
                  if (ek3(ne0, ne1) === ek3(e0, e1)) {
                    if (ne0 === e0 && ne1 === e1) { faces[nj] = na; faces[nj+1] = nc; faces[nj+2] = nb; }
                    break;
                  }
                }
                q.push(ni);
              }
            }
          }
        }

        // Outward normal check
        let mcx = 0, mcy = 0, mcz = 0;
        for (let i = 0; i < wc; i++) { mcx += weldPos[i*3]; mcy += weldPos[i*3+1]; mcz += weldPos[i*3+2]; }
        mcx /= wc; mcy /= wc; mcz /= wc;
        let outCount = 0;
        const sampleStep = Math.max(1, Math.floor(nf / 300));
        for (let fi = 0; fi < nf; fi += sampleStep) {
          const j = fi * 3;
          const a = faces[j], b = faces[j+1], c = faces[j+2];
          const ex1 = weldPos[b*3]-weldPos[a*3], ey1 = weldPos[b*3+1]-weldPos[a*3+1], ez1 = weldPos[b*3+2]-weldPos[a*3+2];
          const ex2 = weldPos[c*3]-weldPos[a*3], ey2 = weldPos[c*3+1]-weldPos[a*3+1], ez2 = weldPos[c*3+2]-weldPos[a*3+2];
          const fnx = ey1*ez2-ez1*ey2, fny = ez1*ex2-ex1*ez2, fnz = ex1*ey2-ey1*ex2;
          const dx = (weldPos[a*3]+weldPos[b*3]+weldPos[c*3])/3-mcx;
          const dy = (weldPos[a*3+1]+weldPos[b*3+1]+weldPos[c*3+1])/3-mcy;
          const dz = (weldPos[a*3+2]+weldPos[b*3+2]+weldPos[c*3+2])/3-mcz;
          if (fnx*dx+fny*dy+fnz*dz > 0) outCount++;
        }
        if (outCount < nf / sampleStep / 2) {
          for (let i = 0; i < faces.length; i += 3) { const tmp = faces[i+1]; faces[i+1] = faces[i+2]; faces[i+2] = tmp; }
        }

        // Replace shellGroup contents with single repaired mesh
        while (shellGroup.children.length > 0) shellGroup.remove(shellGroup.children[0]);
        const finalGeo = new BufferGeometry();
        finalGeo.setAttribute('position', new Float32BufferAttribute(new Float32Array(weldPos), 3));
        finalGeo.setIndex(faces);
        finalGeo.computeVertexNormals();
        finalGeo.computeBoundingSphere();
        shellGroup.add(new ThreeMesh(finalGeo, mat));
      }
    }

    // Export as binary STL for smaller file size
    const stlBuffer = exporter.parse(shellGroup, { binary: true });
    const blob = new Blob([stlBuffer], { type: "application/octet-stream" });
    saveAs(blob, `orthesis-${Date.now()}.stl`);
    showNotification(t("orthesisEditor.stlExportSuccess"), "success");
  }, [showNotification, t]);

  const handleReset = useCallback(() => {
    setFile(null);
    setFileType(null);
    setTextureFile(null);
    setModelBounds(null);
    setCurrentStep(0);
    setUpperCutY(null);
    setLowerCutY(null);
    setUpperTiltX(0);
    setUpperTiltZ(0);
    setLowerTiltX(0);
    setLowerTiltZ(0);
    setThumbEnabled(false);
    setThumbBase(null);
    setThumbTip(null);
    setThumbRingPos(0);
    setClickMode(null);
    setUpperClickPoint(null);
    setLowerClickPoint(null);
    setUpperPoint(null);
    setUpperNormal(null);
    setLowerPoint(null);
    setLowerNormal(null);
    setThumbTiltX(0);
    setThumbTiltZ(0);
    setThumbMode('free');
    setAlignPoint1(null);
    setAlignPoint2(null);
    setIsAligned(false);
    setWallThickness(2.5);
    setSkinOffset(0.3);
    setClosureCount(2);
    // Reset clears the choice — user must explicitly pick a pattern on the
    // hole-design step before any holes are generated.
    setDesignPattern(null);
  }, []);

  if (!isDemo) {
    return (
      <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="text-center p-8">
          <Bone className="mx-auto mb-4 text-slate-300 dark:text-slate-600" size={48} />
          <h2 className="text-xl font-bold text-slate-700 dark:text-slate-300 mb-2">{t("orthesisEditor.comingSoon")}</h2>
          <p className="text-slate-500 dark:text-slate-400">{t("orthesisEditor.demoOnly")}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${scrollUnlocked ? 'min-h-[calc(100vh-5rem)] min-h-[calc(100dvh-5rem)] lg:h-[calc(100vh-5rem)] lg:overflow-hidden' : 'h-[calc(100vh-5rem)] h-[calc(100dvh-5rem)] overflow-hidden'} bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-sans select-none flex flex-col overscroll-contain`}
      style={{ touchAction: 'manipulation', overscrollBehavior: 'contain' }}
    >
      {/* Touch-friendly value picker (mobile/tablet slider replacement).
          Always rendered at the top so it portals out cleanly. */}
      <ValuePickerSheet
        open={valuePicker != null}
        title={valuePicker?.title}
        options={valuePicker?.options}
        value={valuePicker?.value}
        formatLabel={valuePicker?.formatLabel}
        onSelect={(v) => { valuePicker?.onSelect?.(v); closePicker(); }}
        onClose={closePicker}
      />
      {/* Processing overlay — shown while heavy hole-pattern work (line
          preview drawing + CSG cut) is on the main thread. Prevents the user
          from seeing an unresponsive UI during the operation. */}
      {isProcessingHoles && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm pointer-events-auto">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-2xl flex flex-col items-center gap-4 max-w-md w-[min(90vw,28rem)] mx-4 border border-slate-200 dark:border-slate-700">
            <div className="w-12 h-12 border-4 border-[#0f7699] dark:border-[#4fc3f7] border-t-transparent rounded-full animate-spin" />
            <p className="text-slate-800 dark:text-slate-100 font-semibold text-center text-base">
              {processingPhase === 'cutting'
                ? 'Löcher werden in die Orthese ausgeschnitten…'
                : 'Lochmuster wird geplant…'}
            </p>
            <p className="text-slate-500 dark:text-slate-400 text-sm text-center">
              {processingPhase === 'cutting'
                ? 'Die Schnitte werden Stück für Stück angewendet.'
                : 'Positionen werden berechnet und auf die Orthese projiziert.'}
            </p>
            {/* Progress bar — real progress for both planning (async per-row) and cutting (per-hole CSG).
                Bar width and percentage are derived from the SAME rounded value so they stay in sync. */}
            {(() => {
              const pct = Math.max(0, Math.min(100, Math.round(processingProgress)));
              return (
                <div className="w-full mt-2">
                  <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#0f7699] dark:bg-[#4fc3f7] rounded-full"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <p className="text-[10px] text-slate-400 dark:text-slate-500 text-center mt-1 font-mono tabular-nums">
                    {pct} %
                  </p>
                </div>
              );
            })()}
          </div>
        </div>
      )}
      {/* Page Header */}
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 shrink-0">
        <div className="px-3 sm:px-6 lg:px-8 py-2 sm:py-5">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-14 sm:h-14 bg-[#0f7699]/10 dark:bg-[#4fc3f7]/15 rounded-xl sm:rounded-2xl flex items-center justify-center shrink-0">
              <Bone className="text-[#0f7699] dark:text-[#4fc3f7]" size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg sm:text-2xl md:text-3xl font-black text-[#0f7699] dark:text-[#4fc3f7] tracking-tight">
                {t("orthesisEditor.pageTitle")}
              </h1>
              <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium mt-0.5 hidden sm:block">
                {t("orthesisEditor.pageSubtitle")}
              </p>
            </div>
            {/* File info in header */}
            {file && (
              <div className="hidden md:flex items-center gap-3 px-4 py-2.5 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                <div className="w-9 h-9 bg-[#0f7699]/10 dark:bg-[#4fc3f7]/15 rounded-lg flex items-center justify-center shrink-0">
                  <Bone className="text-[#0f7699] dark:text-[#4fc3f7]" size={18} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-bold text-slate-700 dark:text-slate-200 truncate max-w-[200px]">{file.name}</p>
                  {textureFile && (
                    <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate max-w-[200px]">+ {textureFile.name}</p>
                  )}
                  {!textureFile && (
                    <p className="text-[10px] text-slate-500 dark:text-slate-400">{(file.size / 1024 / 1024).toFixed(1)} MB</p>
                  )}
                </div>
                <button onClick={handleReset} className="ml-2 px-3 py-1.5 bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors">
                  {t("orthesisEditor.newScan")}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Layout */}
      <div className="flex flex-col lg:flex-row flex-1 min-h-0 overflow-hidden">
        {/* Workflow Stepper Sidebar */}
        <nav className="w-full lg:w-64 shrink-0 border-b lg:border-b-0 lg:border-r border-slate-200 dark:border-slate-800 px-3 py-2 lg:p-6 lg:pt-8 bg-white dark:bg-slate-900/50 z-30 flex flex-col lg:overflow-y-auto">
          <h3 className="hidden lg:block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-8">
            {t("orthesisEditor.workflowTitle")}
          </h3>

          {/* Mobile stepper */}
          <div className="flex lg:hidden items-center justify-center gap-0 pb-1">
            {steps.map((step, idx) => {
              const isCompleted = currentStep > idx;
              const isActive = currentStep === idx;
              return (
                <div key={step.id} className="flex items-center">
                  <div className={`shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center ${
                    isCompleted
                      ? "bg-[#0f7699]/10 dark:bg-[#4fc3f7]/15 border-[#0f7699] dark:border-[#4fc3f7] text-[#0f7699] dark:text-[#4fc3f7]"
                      : isActive
                        ? "bg-[#0f7699]/5 dark:bg-[#4fc3f7]/10 border-[#0f7699] dark:border-[#4fc3f7] text-[#0f7699] dark:text-[#4fc3f7] ring-2 ring-[#0f7699]/20"
                        : "bg-slate-50 dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-400"
                  }`}>
                    {isCompleted ? <Check size={12} /> : <span className="text-[10px] font-semibold">{idx + 1}</span>}
                  </div>
                  {idx < steps.length - 1 && (
                    <div className={`w-5 h-0.5 ${isCompleted ? 'bg-[#0f7699]' : 'bg-slate-200 dark:bg-slate-700'}`} />
                  )}
                </div>
              );
            })}
          </div>
          <div className="lg:hidden mt-1 text-center">
            <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
              {steps[Math.min(currentStep, steps.length - 1)].label}
            </span>
          </div>

          {/* Desktop stepper */}
          <div className="hidden lg:block relative space-y-8">
            <div className="absolute left-[19px] top-5 bottom-5 w-0.5 bg-slate-200 dark:bg-slate-800" />
            {steps.map((step, idx) => (
              <WorkflowStep
                key={step.id}
                step={step}
                index={idx}
                isCompleted={currentStep > idx}
                isActive={currentStep === idx}
                onClick={idx < currentStep ? () => setCurrentStep(idx) : undefined}
              />
            ))}
          </div>
        </nav>

        {/* Main Content */}
        <main className="flex-1 flex flex-col bg-slate-50 dark:bg-slate-950 relative min-h-0 overflow-hidden">
          <section className={`flex-1 flex flex-col p-3 md:p-6 min-h-0 ${!file ? 'items-center justify-center overflow-y-auto' : 'overflow-hidden'}`}>

            {/* Step 0: Upload */}
            {!file ? (
              <div
                className="max-w-lg w-full p-8 md:p-12 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-2xl bg-white dark:bg-slate-900 text-center hover:border-[#0f7699] dark:hover:border-[#4fc3f7] transition-colors cursor-pointer group"
                onClick={() => document.getElementById('orthesisFileInput').click()}
              >
                <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:bg-[#0f7699]/5 dark:group-hover:bg-[#4fc3f7]/15 transition-colors">
                  <CloudUpload className="text-slate-400 dark:text-slate-500 group-hover:text-[#0f7699] dark:group-hover:text-[#4fc3f7]" size={28} />
                </div>
                <h3 className="text-lg font-bold mb-2 text-[#0f7699] dark:text-[#4fc3f7]">{t("orthesisEditor.uploadTitle")}</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">{t("orthesisEditor.uploadDescription")}</p>
                <button className="px-8 py-3 bg-[#0f7699] dark:bg-[#4fc3f7] text-white dark:text-slate-900 rounded-xl font-bold text-sm hover:bg-[#0f7699]/90 dark:hover:bg-[#4fc3f7]/90 transition-colors shadow-sm">
                  {t("orthesisEditor.selectFile")}
                </button>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-4">{t("orthesisEditor.supportedFormats")}</p>
                <input id="orthesisFileInput" type="file" hidden multiple onChange={handleFileChange} accept=".zip,.stl,.obj,.png" />
              </div>
            ) : (
              /* Steps 1-3: Editor with 3D viewer */
              <div className="w-full flex-1 flex flex-col lg:flex-row gap-2 lg:gap-4 min-h-0 overflow-hidden">

                {/* 3D Viewer — on mobile/tablet the canvas sits at a fixed
                    viewport fraction so the (compressed) controls panel fits
                    below without any body-level scroll. */}
                <div className="h-[38vh] sm:h-[42vh] md:h-[46vh] lg:h-auto lg:flex-1 lg:min-h-0 shrink-0 lg:shrink bg-slate-900 dark:bg-slate-950 rounded-2xl border border-slate-800 dark:border-slate-700 shadow-inner relative overflow-hidden">
                  <div className="absolute inset-0 opacity-10 pointer-events-none">
                    <div className="h-full w-full bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:30px_30px]" />
                  </div>
                  <div className="absolute inset-0">
                    <Canvas className="cursor-move w-full h-full">
                      <OrthesisViewer
                        ref={viewerRef}
                        file={file}
                        fileType={fileType}
                        textureFile={textureFile}
                        upperCutY={upperCutY}
                        lowerCutY={lowerCutY}
                        upperTiltX={upperTiltX}
                        upperTiltZ={upperTiltZ}
                        lowerTiltX={lowerTiltX}
                        lowerTiltZ={lowerTiltZ}
                        upperPoint={upperPoint}
                        upperNormal={upperNormal}
                        lowerPoint={lowerPoint}
                        lowerNormal={lowerNormal}
                        thumbEnabled={!!(thumbBase && thumbTip)}
                        thumbMode={thumbMode}
                        thumbPoint={stableThumbPoint}
                        thumbNormal={stableThumbNormal}
                        thumbBase={currentStep <= 1 ? thumbBase : null}
                        thumbTip={currentStep <= 1 ? thumbTip : null}
                        onModelLoaded={handleModelLoaded}
                        onRingClick={handleRingClick}
                        onAlignClick={handleAlignClick}
                        clickMode={clickMode}
                        upperClickPoint={upperClickPoint}
                        lowerClickPoint={lowerClickPoint}
                        alignPoint1={currentStep === 1 ? alignPoint1 : null}
                        alignPoint2={currentStep === 1 ? alignPoint2 : null}
                        showRings={currentStep >= 2 && !(currentStep >= 3 && hideRings)}
                        showShell={currentStep >= 3}
                        hideScan={currentStep >= 3 && hideScan}
                        skinOffset={skinOffset}
                        wallThickness={wallThickness}
                        slitRotation={currentStep >= 5 ? slitRotation : null}
                        slitWidth={currentStep >= 5 ? slitWidth : null}
                        slitTilt={currentStep >= 5 ? slitTilt : null}
                        slitMarkerRotation={currentStep === 4 ? slitRotation : null}
                        slitMarkerWidth={currentStep === 4 ? slitWidth : null}
                        slitClickPoint={currentStep >= 4 ? slitClickPoint : null}
                        slitClickPoint2={currentStep >= 4 ? slitClickPoint2 : null}
                        closureCount={currentStep >= 4 ? closureCount : 0}
                        holePatternEnabled={currentStep >= 5 ? holePatternEnabled : false}
                        holesPerRow={holesPerRow}
                        holeSpacing={holeSpacing}
                        holePatternApplied={holePatternApplied}
                        onCutProgress={handleCutProgress}
                        onPlanProgress={handlePlanProgress}
                      />
                    </Canvas>
                  </div>
                  {/* Camera reset button */}
                  <button
                    onClick={() => viewerRef.current?.resetCamera()}
                    className="absolute top-3 right-3 w-9 h-9 bg-white/10 hover:bg-white/20 backdrop-blur rounded-lg flex items-center justify-center text-white/70 hover:text-white transition-all"
                    title={t("orthesisEditor.resetCamera")}
                  >
                    <RefreshCcw size={16} />
                  </button>
                  {/* Legend — dynamic based on current step */}
                  <div className="absolute bottom-3 left-3 flex gap-2 flex-wrap">
                    {currentStep === 1 && (
                      <>
                        {alignPoint1 && <span className="px-2 py-1 bg-amber-500/20 backdrop-blur rounded text-[10px] font-bold text-amber-300">A — {t("orthesisEditor.alignPointBottom")}</span>}
                        {alignPoint2 && <span className="px-2 py-1 bg-purple-500/20 backdrop-blur rounded text-[10px] font-bold text-purple-300">B — {t("orthesisEditor.alignPointTop")}</span>}
                        {!alignPoint1 && !alignPoint2 && <span className="px-2 py-1 bg-white/10 backdrop-blur rounded text-[10px] font-bold text-white/50">{t("orthesisEditor.alignHint")}</span>}
                      </>
                    )}
                    {currentStep >= 2 && (
                      <>
                        <span className="px-2 py-1 bg-red-500/20 backdrop-blur rounded text-[10px] font-bold text-red-300">{t("orthesisEditor.upperCutLine")}</span>
                        <span className="px-2 py-1 bg-blue-500/20 backdrop-blur rounded text-[10px] font-bold text-blue-300">{t("orthesisEditor.lowerCutLine")}</span>
                        {thumbEnabled && (
                          <span className="px-2 py-1 bg-green-500/20 backdrop-blur rounded text-[10px] font-bold text-green-300">{t("orthesisEditor.thumbOpening")}</span>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Parameter Panel — content is compressed into a single row
                    of A/B/C/D tiles + tight spacing so all controls fit
                    without scrolling. Panel itself never gets a scrollbar
                    on mobile/tablet — if content somehow doesn't fit, the
                    user toggles the chevron next to Zurück/Weiter to unlock
                    page-level scrolling instead. */}
                <div className="flex w-full flex-1 lg:flex-none lg:w-80 xl:w-96 flex-col min-h-0 overflow-hidden lg:overflow-y-auto gap-2 lg:gap-3">

                  {/* Loading indicator while model bounds not yet available */}
                  {!modelBounds && (
                    <div className="flex items-center justify-center p-8">
                      <div className="text-center">
                        <div className="w-8 h-8 border-2 border-[#0f7699] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                        <p className="text-xs text-slate-500 dark:text-slate-400">{t("orthesisEditor.loadingModel")}</p>
                      </div>
                    </div>
                  )}

                  {/* Step 1: Ausrichtung — split rendering:
                      • MOBILE/TABLET (lg:hidden): compact IconButton-style
                        tiles in one row for A–D (unique labels), with
                        inline status badges.
                      • DESKTOP (hidden lg:flex): original 2-section
                        layout (untouched). */}
                  {currentStep === 1 && modelBounds && (
                    <>
                      {/* Mobile / tablet tile layout */}
                      <div className="lg:hidden">
                        <SectionCard title={t("orthesisEditor.alignTitle")} icon={ArrowRight} color="teal">
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            <EditorTile
                              icon={CircleDot}
                              label={`A — ${t("orthesisEditor.alignPointBottom")}`}
                              isSet={!!alignPoint1}
                              isActive={clickMode === 'align1'}
                              color="amber"
                              onClick={() => { setClickMode('align1'); showNotification(t("orthesisEditor.alignClickBottom"), "info"); }}
                            />
                            <EditorTile
                              icon={CircleDot}
                              label={`B — ${t("orthesisEditor.alignPointTop")}`}
                              isSet={!!alignPoint2}
                              isActive={clickMode === 'align2'}
                              color="purple"
                              onClick={() => { setClickMode('align2'); showNotification(t("orthesisEditor.alignClickTop2"), "info"); }}
                            />
                            <EditorTile
                              icon={CircleDot}
                              label={`C — ${t("orthesisEditor.thumbBasePt")}`}
                              isSet={!!thumbBase}
                              isActive={clickMode === 'thumbBase'}
                              color="orange"
                              onClick={() => { setClickMode('thumbBase'); showNotification(t("orthesisEditor.thumbClickBase"), "info"); }}
                            />
                            <EditorTile
                              icon={CircleDot}
                              label={`D — ${t("orthesisEditor.thumbTipPt")}`}
                              isSet={!!thumbTip}
                              isActive={clickMode === 'thumbTip'}
                              color="green"
                              onClick={() => { setClickMode('thumbTip'); showNotification(t("orthesisEditor.thumbClickTip"), "info"); }}
                            />
                          </div>
                          <div className="flex items-center gap-2 text-[10px] font-bold">
                            <span className={`flex items-center gap-1 px-2 py-1 rounded ${isAligned ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' : 'bg-slate-100 dark:bg-slate-800 text-slate-400'}`}>
                              {isAligned && <Check size={10} />}
                              Arm {isAligned ? '✓' : '○'}
                            </span>
                            <span className={`flex items-center gap-1 px-2 py-1 rounded ${(thumbBase && thumbTip) ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' : 'bg-slate-100 dark:bg-slate-800 text-slate-400'}`}>
                              {(thumbBase && thumbTip) && <Check size={10} />}
                              Daumen {(thumbBase && thumbTip) ? '✓' : '○ (optional)'}
                            </span>
                          </div>
                        </SectionCard>
                      </div>

                      {/* Desktop layout — original two-section design (unchanged) */}
                      <div className="hidden lg:flex flex-col gap-3">
                        <SectionCard title={t("orthesisEditor.alignTitle")} icon={ArrowRight} color="teal">
                          <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                            {t("orthesisEditor.alignDescription")}
                          </p>
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${alignPoint1 ? 'bg-amber-500' : 'bg-slate-300 dark:bg-slate-600'}`} />
                            <span className="text-xs font-bold flex-1 text-slate-600 dark:text-slate-300">
                              A — {t("orthesisEditor.alignPointBottom")}
                            </span>
                            <button
                              onClick={() => { setClickMode('align1'); showNotification(t("orthesisEditor.alignClickBottom"), "info"); }}
                              className={`px-2.5 py-1 rounded text-[10px] font-bold border transition-all ${
                                clickMode === 'align1'
                                  ? 'border-amber-500 bg-amber-500/20 text-amber-400 animate-pulse'
                                  : alignPoint1
                                    ? 'border-amber-400 text-amber-500 hover:bg-amber-500/10'
                                    : 'border-slate-300 dark:border-slate-600 text-slate-500 hover:border-amber-400'
                              }`}
                            >
                              {clickMode === 'align1' ? '...' : alignPoint1 ? '↻' : '⊕'}
                            </button>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${alignPoint2 ? 'bg-purple-500' : 'bg-slate-300 dark:bg-slate-600'}`} />
                            <span className="text-xs font-bold flex-1 text-slate-600 dark:text-slate-300">
                              B — {t("orthesisEditor.alignPointTop")}
                            </span>
                            <button
                              onClick={() => { setClickMode('align2'); showNotification(t("orthesisEditor.alignClickTop2"), "info"); }}
                              className={`px-2.5 py-1 rounded text-[10px] font-bold border transition-all ${
                                clickMode === 'align2'
                                  ? 'border-purple-500 bg-purple-500/20 text-purple-400 animate-pulse'
                                  : alignPoint2
                                    ? 'border-purple-400 text-purple-500 hover:bg-purple-500/10'
                                    : 'border-slate-300 dark:border-slate-600 text-slate-500 hover:border-purple-400'
                              }`}
                            >
                              {clickMode === 'align2' ? '...' : alignPoint2 ? '↻' : '⊕'}
                            </button>
                          </div>
                          {isAligned && (
                            <div className="flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                              <Check size={14} className="text-green-500" />
                              <span className="text-xs font-bold text-green-700 dark:text-green-400">{t("orthesisEditor.alignDone")}</span>
                            </div>
                          )}
                        </SectionCard>
                        <SectionCard title={t("orthesisEditor.thumbAxisTitle")} icon={Hand} color="green">
                          <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                            {t("orthesisEditor.thumbAxisDescription")}
                          </p>
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${thumbBase ? 'bg-orange-500' : 'bg-slate-300 dark:bg-slate-600'}`} />
                            <span className="text-xs font-bold flex-1 text-slate-600 dark:text-slate-300">
                              A — {t("orthesisEditor.thumbBasePt")}
                            </span>
                            <button
                              onClick={() => { setClickMode('thumbBase'); showNotification(t("orthesisEditor.thumbClickBase"), "info"); }}
                              className={`px-2.5 py-1 rounded text-[10px] font-bold border transition-all ${
                                clickMode === 'thumbBase'
                                  ? 'border-orange-500 bg-orange-500/20 text-orange-400 animate-pulse'
                                  : thumbBase
                                    ? 'border-orange-400 text-orange-500 hover:bg-orange-500/10'
                                    : 'border-slate-300 dark:border-slate-600 text-slate-500 hover:border-orange-400'
                              }`}
                            >
                              {clickMode === 'thumbBase' ? '...' : thumbBase ? '↻' : '⊕'}
                            </button>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${thumbTip ? 'bg-purple-500' : 'bg-slate-300 dark:bg-slate-600'}`} />
                            <span className="text-xs font-bold flex-1 text-slate-600 dark:text-slate-300">
                              B — {t("orthesisEditor.thumbTipPt")}
                            </span>
                            <button
                              onClick={() => { setClickMode('thumbTip'); showNotification(t("orthesisEditor.thumbClickTip"), "info"); }}
                              className={`px-2.5 py-1 rounded text-[10px] font-bold border transition-all ${
                                clickMode === 'thumbTip'
                                  ? 'border-purple-500 bg-purple-500/20 text-purple-400 animate-pulse'
                                  : thumbTip
                                    ? 'border-purple-400 text-purple-500 hover:bg-purple-500/10'
                                    : 'border-slate-300 dark:border-slate-600 text-slate-500 hover:border-purple-400'
                              }`}
                            >
                              {clickMode === 'thumbTip' ? '...' : thumbTip ? '↻' : '⊕'}
                            </button>
                          </div>
                          {thumbBase && thumbTip && (
                            <div className="flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                              <Check size={14} className="text-green-500" />
                              <span className="text-xs font-bold text-green-700 dark:text-green-400">{t("orthesisEditor.thumbAxisSet")}</span>
                            </div>
                          )}
                          <p className="text-[10px] text-slate-400 dark:text-slate-500 italic">
                            {t("orthesisEditor.thumbAxisOptional")}
                          </p>
                        </SectionCard>
                      </div>
                    </>
                  )}

                  {/* Step 2: Passform — split rendering:
                      • MOBILE/TABLET: 3 placement EditorTiles (A=upper, B=lower,
                        C=thumb) + value tiles (open a touch picker on tap).
                      • DESKTOP: original CollapsibleRingSection layout (unchanged). */}
                  {currentStep === 2 && modelBounds && (
                    <>
                      {/* Mobile / tablet tile layout */}
                      <div className="lg:hidden">
                        <SectionCard title={t("orthesisEditor.upperCutLine") + ' / ' + t("orthesisEditor.lowerCutLine")} icon={Scissors} color="teal">
                          <div className="grid grid-cols-3 gap-2">
                            <EditorTile
                              icon={CircleDot}
                              label={`A — ${t("orthesisEditor.upperCutLine")}`}
                              isSet={upperClickPoint != null}
                              isActive={clickMode === 'upper'}
                              color="red"
                              onClick={() => { setClickMode(clickMode === 'upper' ? null : 'upper'); if (clickMode !== 'upper') showNotification(t("orthesisEditor.clickToPlace"), "info"); }}
                            />
                            <EditorTile
                              icon={CircleDot}
                              label={`B — ${t("orthesisEditor.lowerCutLine")}`}
                              isSet={lowerClickPoint != null}
                              isActive={clickMode === 'lower'}
                              color="blue"
                              onClick={() => { setClickMode(clickMode === 'lower' ? null : 'lower'); if (clickMode !== 'lower') showNotification(t("orthesisEditor.clickToPlace"), "info"); }}
                            />
                            <EditorTile
                              icon={CircleDot}
                              label={`C — ${t("orthesisEditor.thumbOpening")}`}
                              isSet={thumbEnabled}
                              isActive={clickMode === 'thumbRing'}
                              isDisabled={!thumbBase || !thumbTip}
                              color="green"
                              onClick={() => { setClickMode(clickMode === 'thumbRing' ? null : 'thumbRing'); if (clickMode !== 'thumbRing') showNotification(t("orthesisEditor.thumbClickRing"), "info"); setThumbEnabled(true); }}
                            />
                          </div>
                          <div className="flex items-center justify-between px-2 py-1 bg-[#0f7699]/5 dark:bg-[#4fc3f7]/10 rounded">
                            <span className="text-[10px] text-slate-600 dark:text-slate-400">{t("orthesisEditor.activeHeight")}</span>
                            <span className="text-[11px] font-bold text-[#0f7699] dark:text-[#4fc3f7]">{((upperCutY || 0) - (lowerCutY || 0)).toFixed(1)} mm</span>
                          </div>
                          {/* Value tiles per active line — only for set lines */}
                          {upperClickPoint && (
                            <div className="grid grid-cols-3 gap-2">
                              <EditorValueTile
                                label={`A · ${t("orthesisEditor.position")}`}
                                value={(upperCutY || 0).toFixed(1)}
                                unit="mm"
                                onClick={() => openPicker({
                                  title: `A — ${t("orthesisEditor.position")}`,
                                  options: buildNumericOptions(modelBounds.minY, modelBounds.maxY, 1),
                                  value: parseFloat((upperCutY || 0).toFixed(0)),
                                  formatLabel: (v) => `${v.toFixed(0)} mm`,
                                  onSelect: setUpperCutY,
                                })}
                              />
                              <EditorValueTile
                                label="A · Tilt X"
                                value={(upperTiltX || 0).toFixed(2)}
                                unit="rad"
                                onClick={() => openPicker({
                                  title: 'A — Tilt X',
                                  options: buildNumericOptions(-0.5, 0.5, 0.05),
                                  value: parseFloat((upperTiltX || 0).toFixed(2)),
                                  formatLabel: (v) => v.toFixed(2),
                                  onSelect: setUpperTiltX,
                                })}
                              />
                              <EditorValueTile
                                label="A · Tilt Z"
                                value={(upperTiltZ || 0).toFixed(2)}
                                unit="rad"
                                onClick={() => openPicker({
                                  title: 'A — Tilt Z',
                                  options: buildNumericOptions(-0.5, 0.5, 0.05),
                                  value: parseFloat((upperTiltZ || 0).toFixed(2)),
                                  formatLabel: (v) => v.toFixed(2),
                                  onSelect: setUpperTiltZ,
                                })}
                              />
                            </div>
                          )}
                          {lowerClickPoint && (
                            <div className="grid grid-cols-3 gap-2">
                              <EditorValueTile
                                label={`B · ${t("orthesisEditor.position")}`}
                                value={(lowerCutY || 0).toFixed(1)}
                                unit="mm"
                                onClick={() => openPicker({
                                  title: `B — ${t("orthesisEditor.position")}`,
                                  options: buildNumericOptions(modelBounds.minY, modelBounds.maxY, 1),
                                  value: parseFloat((lowerCutY || 0).toFixed(0)),
                                  formatLabel: (v) => `${v.toFixed(0)} mm`,
                                  onSelect: setLowerCutY,
                                })}
                              />
                              <EditorValueTile
                                label="B · Tilt X"
                                value={(lowerTiltX || 0).toFixed(2)}
                                unit="rad"
                                onClick={() => openPicker({
                                  title: 'B — Tilt X',
                                  options: buildNumericOptions(-0.5, 0.5, 0.05),
                                  value: parseFloat((lowerTiltX || 0).toFixed(2)),
                                  formatLabel: (v) => v.toFixed(2),
                                  onSelect: setLowerTiltX,
                                })}
                              />
                              <EditorValueTile
                                label="B · Tilt Z"
                                value={(lowerTiltZ || 0).toFixed(2)}
                                unit="rad"
                                onClick={() => openPicker({
                                  title: 'B — Tilt Z',
                                  options: buildNumericOptions(-0.5, 0.5, 0.05),
                                  value: parseFloat((lowerTiltZ || 0).toFixed(2)),
                                  formatLabel: (v) => v.toFixed(2),
                                  onSelect: setLowerTiltZ,
                                })}
                              />
                            </div>
                          )}
                          {thumbBase && thumbTip && thumbEnabled && (
                            <div className="grid grid-cols-3 gap-2">
                              <EditorValueTile
                                label={`C · ${t("orthesisEditor.thumbRingPosition")}`}
                                value={(thumbRingPos || 0).toFixed(2)}
                                onClick={() => openPicker({
                                  title: `C — ${t("orthesisEditor.thumbRingPosition")}`,
                                  options: buildNumericOptions(-1.2, 1.2, 0.1),
                                  value: parseFloat((thumbRingPos || 0).toFixed(1)),
                                  formatLabel: (v) => v.toFixed(1),
                                  onSelect: setThumbRingPos,
                                })}
                              />
                              <EditorValueTile
                                label="C · Tilt X"
                                value={(thumbTiltX || 0).toFixed(2)}
                                unit="rad"
                                onClick={() => openPicker({
                                  title: 'C — Tilt X',
                                  options: buildNumericOptions(-0.78, 0.78, 0.05),
                                  value: parseFloat((thumbTiltX || 0).toFixed(2)),
                                  formatLabel: (v) => v.toFixed(2),
                                  onSelect: setThumbTiltX,
                                })}
                              />
                              <EditorValueTile
                                label="C · Tilt Z"
                                value={(thumbTiltZ || 0).toFixed(2)}
                                unit="rad"
                                onClick={() => openPicker({
                                  title: 'C — Tilt Z',
                                  options: buildNumericOptions(-0.78, 0.78, 0.05),
                                  value: parseFloat((thumbTiltZ || 0).toFixed(2)),
                                  formatLabel: (v) => v.toFixed(2),
                                  onSelect: setThumbTiltZ,
                                })}
                              />
                            </div>
                          )}
                        </SectionCard>
                      </div>

                      {/* Desktop layout — original (unchanged) */}
                      <div className="hidden lg:flex flex-col gap-3">
                        <CollapsibleRingSection
                          title={t("orthesisEditor.upperCutLine")}
                          color="red"
                          isSet={upperClickPoint != null}
                          onPlace={() => { setClickMode(clickMode === 'upper' ? null : 'upper'); if (clickMode !== 'upper') showNotification(t("orthesisEditor.clickToPlace"), "info"); }}
                          isPlacing={clickMode === 'upper'}
                        >
                          <ParamSlider label={t("orthesisEditor.position")} value={upperCutY} onChange={setUpperCutY} min={modelBounds.minY} max={modelBounds.maxY} step={0.1} />
                          <ParamSlider label={t("orthesisEditor.upperTiltX")} value={upperTiltX} onChange={setUpperTiltX} min={-0.5} max={0.5} step={0.01} unit="rad" />
                          <ParamSlider label={t("orthesisEditor.upperTiltZ")} value={upperTiltZ} onChange={setUpperTiltZ} min={-0.5} max={0.5} step={0.01} unit="rad" />
                        </CollapsibleRingSection>
                        <CollapsibleRingSection
                          title={t("orthesisEditor.lowerCutLine")}
                          color="blue"
                          isSet={lowerClickPoint != null}
                          onPlace={() => { setClickMode(clickMode === 'lower' ? null : 'lower'); if (clickMode !== 'lower') showNotification(t("orthesisEditor.clickToPlace"), "info"); }}
                          isPlacing={clickMode === 'lower'}
                        >
                          <ParamSlider label={t("orthesisEditor.position")} value={lowerCutY} onChange={setLowerCutY} min={modelBounds.minY} max={modelBounds.maxY} step={0.1} />
                          <ParamSlider label={t("orthesisEditor.lowerTiltX")} value={lowerTiltX} onChange={setLowerTiltX} min={-0.5} max={0.5} step={0.01} unit="rad" />
                          <ParamSlider label={t("orthesisEditor.lowerTiltZ")} value={lowerTiltZ} onChange={setLowerTiltZ} min={-0.5} max={0.5} step={0.01} unit="rad" />
                        </CollapsibleRingSection>
                        <div className="flex items-center justify-between px-3 py-2 bg-[#0f7699]/5 dark:bg-[#4fc3f7]/10 rounded-lg">
                          <span className="text-xs text-slate-600 dark:text-slate-400">{t("orthesisEditor.activeHeight")}</span>
                          <span className="text-xs font-bold text-[#0f7699] dark:text-[#4fc3f7]">
                            {((upperCutY || 0) - (lowerCutY || 0)).toFixed(1)} mm
                          </span>
                        </div>
                        {thumbBase && thumbTip && (
                          <CollapsibleRingSection
                            title={t("orthesisEditor.thumbOpening")}
                            color="green"
                            isSet={thumbEnabled}
                            onPlace={() => { setClickMode(clickMode === 'thumbRing' ? null : 'thumbRing'); if (clickMode !== 'thumbRing') showNotification(t("orthesisEditor.thumbClickRing"), "info"); setThumbEnabled(true); }}
                            isPlacing={clickMode === 'thumbRing'}
                          >
                            <ParamSlider
                              label={t("orthesisEditor.thumbRingPosition")}
                              value={thumbRingPos}
                              onChange={setThumbRingPos}
                              min={-1.2}
                              max={1.2}
                              step={0.01}
                            />
                            <ParamSlider label={t("orthesisEditor.thumbTiltX")} value={thumbTiltX} onChange={setThumbTiltX} min={-0.785} max={0.785} step={0.01} unit="rad" />
                            <ParamSlider label={t("orthesisEditor.thumbTiltZ")} value={thumbTiltZ} onChange={setThumbTiltZ} min={-0.785} max={0.785} step={0.01} unit="rad" />
                          </CollapsibleRingSection>
                        )}
                        {!thumbBase || !thumbTip ? (
                          <div className="px-3 py-2 bg-slate-100 dark:bg-slate-800/50 rounded-lg">
                            <p className="text-[10px] text-slate-400 dark:text-slate-500 italic">
                              {t("orthesisEditor.thumbAxisHint")}
                            </p>
                          </div>
                        ) : null}
                      </div>
                    </>
                  )}

                  {/* Step 2: Material & Design */}
                  {currentStep === 3 && (
                    <>
                      {/* Mobile / tablet tile layout */}
                      <div className="lg:hidden">
                        <SectionCard title={t("orthesisEditor.materialSection")} icon={Layers} color="teal">
                          <div className="grid grid-cols-2 gap-2">
                            <EditorValueTile
                              label={t("orthesisEditor.wallThickness")}
                              value={wallThickness.toFixed(1)}
                              unit="mm"
                              onClick={() => openPicker({
                                title: t("orthesisEditor.wallThickness"),
                                options: buildNumericOptions(1.0, 5.0, 0.5),
                                value: parseFloat(wallThickness.toFixed(1)),
                                formatLabel: (v) => `${v.toFixed(1)} mm`,
                                onSelect: setWallThickness,
                              })}
                            />
                            <EditorValueTile
                              label={t("orthesisEditor.skinOffset")}
                              value={skinOffset.toFixed(2)}
                              unit="mm"
                              onClick={() => openPicker({
                                title: t("orthesisEditor.skinOffset"),
                                options: buildNumericOptions(0.1, 1.5, 0.1),
                                value: parseFloat(skinOffset.toFixed(1)),
                                formatLabel: (v) => `${v.toFixed(1)} mm`,
                                onSelect: setSkinOffset,
                              })}
                            />
                          </div>
                          <div className="flex flex-wrap gap-3 text-[11px]">
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input type="checkbox" checked={hideScan} onChange={(e) => setHideScan(e.target.checked)} className="w-4 h-4 rounded accent-[#0f7699] dark:accent-[#4fc3f7]" />
                              <span className="text-slate-600 dark:text-slate-400">{t("orthesisEditor.hideScan")}</span>
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input type="checkbox" checked={hideRings} onChange={(e) => setHideRings(e.target.checked)} className="w-4 h-4 rounded accent-[#0f7699] dark:accent-[#4fc3f7]" />
                              <span className="text-slate-600 dark:text-slate-400">{t("orthesisEditor.hideRings")}</span>
                            </label>
                          </div>
                        </SectionCard>
                      </div>

                      {/* Desktop layout — original (unchanged) */}
                      <div className="hidden lg:flex flex-col gap-3">
                        <SectionCard title={t("orthesisEditor.materialSection")} icon={Layers} color="teal">
                          <ParamSlider
                            label={t("orthesisEditor.wallThickness")}
                            value={wallThickness}
                            onChange={setWallThickness}
                            min={1.0}
                            max={5.0}
                            step={0.1}
                          />
                          <ParamSlider
                            label={t("orthesisEditor.skinOffset")}
                            value={skinOffset}
                            onChange={setSkinOffset}
                            min={0.1}
                            max={1.5}
                            step={0.05}
                          />
                        </SectionCard>
                        <div className="flex flex-col gap-2 px-1">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={hideScan}
                              onChange={(e) => setHideScan(e.target.checked)}
                              className="w-4 h-4 rounded accent-[#0f7699] dark:accent-[#4fc3f7]"
                            />
                            <span className="text-xs text-slate-600 dark:text-slate-400">{t("orthesisEditor.hideScan")}</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={hideRings}
                              onChange={(e) => setHideRings(e.target.checked)}
                              className="w-4 h-4 rounded accent-[#0f7699] dark:accent-[#4fc3f7]"
                            />
                            <span className="text-xs text-slate-600 dark:text-slate-400">{t("orthesisEditor.hideRings")}</span>
                          </label>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Step 4: Öffnung */}
                  {currentStep === 4 && (
                    <>
                      {/* Mobile / tablet tile layout */}
                      <div className="lg:hidden">
                        <SectionCard title={t("orthesisEditor.openingSection") || "Öffnung"} icon={SplitSquareHorizontal} color="teal">
                          <div className="grid grid-cols-4 gap-2">
                            <EditorTile
                              icon={CircleDot}
                              label={`A — ${t("orthesisEditor.slitPointA") || "Position"}`}
                              isSet={!!slitClickPoint}
                              isActive={clickMode === 'slitPlace'}
                              color="orange"
                              onClick={() => { setClickMode(clickMode === 'slitPlace' ? null : 'slitPlace'); if (clickMode !== 'slitPlace') showNotification(t("orthesisEditor.slitClickHint") || "Klicke auf die Orthese", "info"); }}
                            />
                            <EditorTile
                              icon={CircleDot}
                              label={`B — ${t("orthesisEditor.slitPointB") || "Neigung"}`}
                              isSet={!!slitClickPoint2}
                              isActive={clickMode === 'slitPlace2'}
                              color="purple"
                              onClick={() => { setClickMode(clickMode === 'slitPlace2' ? null : 'slitPlace2'); if (clickMode !== 'slitPlace2') showNotification(t("orthesisEditor.slitClickTiltHint") || "Klicke auf die Orthese", "info"); }}
                            />
                            <EditorValueTile
                              label={t("orthesisEditor.slitWidth") || "Breite"}
                              value={slitWidth.toFixed(1)}
                              unit="mm"
                              onClick={() => openPicker({
                                title: t("orthesisEditor.slitWidth") || "Schlitzbreite",
                                options: buildNumericOptions(2, 30, 1),
                                value: Math.round(slitWidth),
                                formatLabel: (v) => `${v} mm`,
                                onSelect: setSlitWidth,
                              })}
                            />
                            <EditorValueTile
                              label={t("orthesisEditor.closureCount")}
                              value={closureCount}
                              unit={t("orthesisEditor.pieces")}
                              onClick={() => openPicker({
                                title: t("orthesisEditor.closureCount"),
                                options: [0, 1, 2, 3, 4],
                                value: closureCount,
                                onSelect: (v) => setClosureCount(Math.round(v)),
                              })}
                            />
                          </div>
                          <div className="flex flex-wrap gap-3 text-[11px]">
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input type="checkbox" checked={hideScan} onChange={(e) => setHideScan(e.target.checked)} className="w-4 h-4 rounded accent-[#0f7699] dark:accent-[#4fc3f7]" />
                              <span className="text-slate-600 dark:text-slate-400">{t("orthesisEditor.hideScan")}</span>
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input type="checkbox" checked={hideRings} onChange={(e) => setHideRings(e.target.checked)} className="w-4 h-4 rounded accent-[#0f7699] dark:accent-[#4fc3f7]" />
                              <span className="text-slate-600 dark:text-slate-400">{t("orthesisEditor.hideRings")}</span>
                            </label>
                          </div>
                        </SectionCard>
                      </div>

                      {/* Desktop layout — original (unchanged) */}
                      <div className="hidden lg:flex flex-col gap-3">
                        <SectionCard title={t("orthesisEditor.openingSection") || "Öffnung"} icon={SplitSquareHorizontal} color="teal">
                          <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                            {t("orthesisEditor.slitDescription") || "Setze zwei Punkte auf der Orthese: A bestimmt die Position, B die Neigung des Schlitzes."}
                          </p>
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${slitClickPoint ? 'bg-orange-500' : 'bg-slate-300 dark:bg-slate-600'}`} />
                            <span className="text-xs font-bold flex-1 text-slate-600 dark:text-slate-300">
                              A — {t("orthesisEditor.slitPointA") || "Position"}
                            </span>
                            <button
                              onClick={() => { setClickMode(clickMode === 'slitPlace' ? null : 'slitPlace'); if (clickMode !== 'slitPlace') showNotification(t("orthesisEditor.slitClickHint") || "Klicke auf die Orthese für die Schlitzposition", "info"); }}
                              className={`px-2.5 py-1 rounded text-[10px] font-bold border transition-all ${
                                clickMode === 'slitPlace'
                                  ? 'border-orange-500 bg-orange-500/20 text-orange-400 animate-pulse'
                                  : slitClickPoint
                                    ? 'border-orange-400 text-orange-500 hover:bg-orange-500/10'
                                    : 'border-slate-300 dark:border-slate-600 text-slate-500 hover:border-orange-400'
                              }`}
                            >
                              {clickMode === 'slitPlace' ? '...' : slitClickPoint ? '↻' : '⊕'}
                            </button>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${slitClickPoint2 ? 'bg-purple-500' : 'bg-slate-300 dark:bg-slate-600'}`} />
                            <span className="text-xs font-bold flex-1 text-slate-600 dark:text-slate-300">
                              B — {t("orthesisEditor.slitPointB") || "Neigung"}
                            </span>
                            <button
                              onClick={() => { setClickMode(clickMode === 'slitPlace2' ? null : 'slitPlace2'); if (clickMode !== 'slitPlace2') showNotification(t("orthesisEditor.slitClickTiltHint") || "Klicke auf die Orthese für die Neigungsrichtung", "info"); }}
                              className={`px-2.5 py-1 rounded text-[10px] font-bold border transition-all ${
                                clickMode === 'slitPlace2'
                                  ? 'border-purple-500 bg-purple-500/20 text-purple-400 animate-pulse'
                                  : slitClickPoint2
                                    ? 'border-purple-400 text-purple-500 hover:bg-purple-500/10'
                                    : 'border-slate-300 dark:border-slate-600 text-slate-500 hover:border-purple-400'
                              }`}
                            >
                              {clickMode === 'slitPlace2' ? '...' : slitClickPoint2 ? '↻' : '⊕'}
                            </button>
                          </div>
                          {slitClickPoint && slitClickPoint2 && (
                            <div className="flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                              <Check size={14} className="text-green-500" />
                              <span className="text-xs font-bold text-green-700 dark:text-green-400">{t("orthesisEditor.slitAxisSet") || "Schlitzachse definiert"}</span>
                            </div>
                          )}
                          <ParamSlider
                            label={t("orthesisEditor.slitWidth") || "Schlitzbreite"}
                            value={slitWidth}
                            onChange={setSlitWidth}
                            min={2}
                            max={30}
                            step={0.5}
                          />
                        </SectionCard>
                        {slitClickPoint && (
                          <SectionCard title={t("orthesisEditor.closureSection") || "Verschlüsse"} icon={Settings2} color="blue">
                            <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                              {t("orthesisEditor.closureDescription") || "Klettverschluss-Halter links und rechts vom Schlitz."}
                            </p>
                            <ParamSlider
                              label={t("orthesisEditor.closureCount")}
                              value={closureCount}
                              onChange={(v) => setClosureCount(Math.round(v))}
                              min={0}
                              max={4}
                              step={1}
                              unit={t("orthesisEditor.pieces")}
                            />
                          </SectionCard>
                        )}
                        <div className="flex flex-col gap-2 px-1">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={hideScan} onChange={(e) => setHideScan(e.target.checked)} className="w-4 h-4 rounded accent-[#0f7699] dark:accent-[#4fc3f7]" />
                            <span className="text-xs text-slate-600 dark:text-slate-400">{t("orthesisEditor.hideScan")}</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={hideRings} onChange={(e) => setHideRings(e.target.checked)} className="w-4 h-4 rounded accent-[#0f7699] dark:accent-[#4fc3f7]" />
                            <span className="text-xs text-slate-600 dark:text-slate-400">{t("orthesisEditor.hideRings")}</span>
                          </label>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Step 5: Lochmuster — pattern selection drives the entire
                      step. "Keine" (none) means no holes are generated at all
                      (CSG step is skipped on Next). Picking a real pattern
                      automatically enables generation and shows the preview. */}
                  {currentStep === 5 && (() => {
                    const patterns = [
                      { key: 'none', available: true },
                      { key: 'fdm', available: true },
                      { key: 'voronoi', available: false },
                      { key: 'hexagon', available: false },
                      { key: 'organic', available: false },
                      { key: 'lines', available: false },
                    ];
                    const selectPattern = (pattern, available) => {
                      if (!available) return;
                      if (pattern === 'none') {
                        setDesignPattern(pattern);
                        setHolePatternEnabled(false);
                        return;
                      }
                      setProcessingPhase('planning');
                      setProcessingProgress(0);
                      setIsProcessingHoles(true);
                      requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                          setDesignPattern(pattern);
                          setHolePatternEnabled(true);
                        });
                      });
                    };
                    return (
                      <>
                        {/* Mobile / tablet tile layout */}
                        <div className="lg:hidden">
                          <SectionCard title="Lochmuster" icon={Settings2} color="teal">
                            <div className="grid grid-cols-3 gap-2">
                              {patterns.map(({ key: pattern, available }) => (
                                <EditorTile
                                  key={pattern}
                                  label={t(`orthesisEditor.pattern_${pattern}`)}
                                  sublabel={!available ? t("orthesisEditor.patternSoon", "bald") : undefined}
                                  isSet={designPattern === pattern && available}
                                  isDisabled={!available}
                                  color="teal"
                                  onClick={() => selectPattern(pattern, available)}
                                  icon={Settings2}
                                />
                              ))}
                            </div>
                            {holePatternEnabled && designPattern && designPattern !== 'none' && (
                              <div className="grid grid-cols-2 gap-2">
                                <EditorValueTile
                                  label="Löcher pro Reihe"
                                  value={holesPerRow}
                                  onClick={() => openPicker({
                                    title: 'Löcher pro Reihe',
                                    options: [1, 2, 3, 4, 5, 6, 7, 8],
                                    value: holesPerRow,
                                    onSelect: setHolesPerRow,
                                  })}
                                />
                                <EditorValueTile
                                  label="Lochabstand"
                                  value={holeSpacing}
                                  unit="mm"
                                  onClick={() => openPicker({
                                    title: 'Lochabstand axial',
                                    options: buildNumericOptions(8, 30, 2),
                                    value: holeSpacing,
                                    formatLabel: (v) => `${v} mm`,
                                    onSelect: setHoleSpacing,
                                  })}
                                />
                              </div>
                            )}
                            {!designPattern && (
                              <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-relaxed">
                                {t("orthesisEditor.patternPickPrompt", "Bitte wähle ein Lochmuster oder „Keine\" aus, um fortzufahren.")}
                              </p>
                            )}
                          </SectionCard>
                        </div>

                        {/* Desktop layout — original (unchanged) */}
                        <div className="hidden lg:flex flex-col gap-3">
                          <SectionCard title="Lochmuster" icon={Settings2} color="teal">
                            <div className="space-y-4">
                              <div className="space-y-2">
                                <label className="text-xs font-bold text-slate-700 dark:text-slate-200">{t("orthesisEditor.designPattern")}</label>
                                <div className="grid grid-cols-2 gap-2">
                                  {patterns.map(({ key: pattern, available }) => {
                                    const isActive = designPattern === pattern;
                                    return (
                                      <button
                                        key={pattern}
                                        onClick={() => selectPattern(pattern, available)}
                                        disabled={!available}
                                        title={!available ? t("orthesisEditor.patternComingSoon", "Bald verfügbar") : undefined}
                                        className={`relative px-3 py-2 rounded-lg text-xs font-bold border transition-all ${
                                          isActive && available
                                            ? 'border-[#0f7699] dark:border-[#4fc3f7] bg-[#0f7699]/10 dark:bg-[#4fc3f7]/15 text-[#0f7699] dark:text-[#4fc3f7]'
                                            : available
                                              ? 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:border-slate-300'
                                              : 'border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800/60 text-slate-300 dark:text-slate-600 cursor-not-allowed'
                                        }`}
                                      >
                                        {t(`orthesisEditor.pattern_${pattern}`)}
                                        {!available && (
                                          <span className="block text-[9px] font-medium uppercase tracking-wider text-slate-400 dark:text-slate-500 mt-0.5">
                                            {t("orthesisEditor.patternSoon", "bald")}
                                          </span>
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                                {!designPattern && (
                                  <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-relaxed pt-1">
                                    {t("orthesisEditor.patternPickPrompt", "Bitte wähle ein Lochmuster oder „Keine\" aus, um fortzufahren.")}
                                  </p>
                                )}
                              </div>
                              {holePatternEnabled && designPattern && designPattern !== 'none' && (
                                <>
                                  <div className="space-y-2">
                                    <div className="flex justify-between">
                                      <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Löcher pro Reihe</span>
                                      <span className="text-xs font-bold text-[#0f7699] dark:text-[#4fc3f7]">{holesPerRow}</span>
                                    </div>
                                    <input
                                      type="range"
                                      min={1}
                                      max={8}
                                      step={1}
                                      value={holesPerRow}
                                      onChange={(e) => setHolesPerRow(parseInt(e.target.value))}
                                      className="w-full h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full appearance-none cursor-pointer accent-[#0f7699] dark:accent-[#4fc3f7]"
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <div className="flex justify-between">
                                      <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Lochabstand axial</span>
                                      <span className="text-xs font-bold text-[#0f7699] dark:text-[#4fc3f7]">{holeSpacing} mm</span>
                                    </div>
                                    <input
                                      type="range"
                                      min={8}
                                      max={30}
                                      step={1}
                                      value={holeSpacing}
                                      onChange={(e) => setHoleSpacing(parseInt(e.target.value))}
                                      className="w-full h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full appearance-none cursor-pointer accent-[#0f7699] dark:accent-[#4fc3f7]"
                                    />
                                  </div>
                                </>
                              )}
                            </div>
                          </SectionCard>
                        </div>
                      </>
                    );
                  })()}

                  {/* Step 6: Export */}
                  {currentStep === 6 && (
                    <SectionCard title={t("orthesisEditor.parameterSummary")} icon={FileDown} color="teal">
                      {/* Summary */}
                      <div className="space-y-2 text-xs">
                        <div className="flex justify-between py-1.5 border-b border-slate-100 dark:border-slate-700">
                          <span className="text-slate-500">{t("orthesisEditor.activeHeight")}</span>
                          <span className="font-bold text-slate-700 dark:text-slate-200">{((upperCutY || 0) - (lowerCutY || 0)).toFixed(1)} mm</span>
                        </div>
                        <div className="flex justify-between py-1.5 border-b border-slate-100 dark:border-slate-700">
                          <span className="text-slate-500">{t("orthesisEditor.thumbOpening")}</span>
                          <span className="font-bold text-slate-700 dark:text-slate-200">{thumbEnabled ? `${t("orthesisEditor." + thumbMode === 'free' ? 'thumbFree' : 'thumbFixed')}` : t("orthesisEditor.disabled")}</span>
                        </div>
                        <div className="flex justify-between py-1.5 border-b border-slate-100 dark:border-slate-700">
                          <span className="text-slate-500">{t("orthesisEditor.wallThickness")}</span>
                          <span className="font-bold text-slate-700 dark:text-slate-200">{wallThickness.toFixed(1)} mm</span>
                        </div>
                        <div className="flex justify-between py-1.5 border-b border-slate-100 dark:border-slate-700">
                          <span className="text-slate-500">{t("orthesisEditor.skinOffset")}</span>
                          <span className="font-bold text-slate-700 dark:text-slate-200">{skinOffset.toFixed(2)} mm</span>
                        </div>
                        <div className="flex justify-between py-1.5 border-b border-slate-100 dark:border-slate-700">
                          <span className="text-slate-500">{t("orthesisEditor.closureCount")}</span>
                          <span className="font-bold text-slate-700 dark:text-slate-200">{closureCount}</span>
                        </div>
                        <div className="flex justify-between py-1.5">
                          <span className="text-slate-500">{t("orthesisEditor.designPattern")}</span>
                          <span className="font-bold text-slate-700 dark:text-slate-200">{t(`orthesisEditor.pattern_${designPattern}`)}</span>
                        </div>
                      </div>

                      {/* Export buttons */}
                      <div className="flex flex-col gap-2 mt-4">
                        <button
                          onClick={handleExportJson}
                          className="w-full px-4 py-3 bg-[#0f7699] dark:bg-[#4fc3f7] text-white dark:text-slate-900 rounded-xl font-bold text-sm hover:bg-[#0f7699]/90 dark:hover:bg-[#4fc3f7]/90 transition-colors flex items-center justify-center gap-2"
                        >
                          <Download size={16} />
                          {t("orthesisEditor.exportJson")}
                        </button>
                        <button
                          onClick={handleExportOrthesisStl}
                          className="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl font-bold text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center justify-center gap-2"
                        >
                          <FileDown size={16} />
                          {t("orthesisEditor.exportOrthesisStl")}
                        </button>
                      </div>

                      {/* Phase 2 hint */}
                      <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                        <p className="text-[11px] text-amber-700 dark:text-amber-400 font-medium">
                          {t("orthesisEditor.phase2Hint")}
                        </p>
                      </div>
                    </SectionCard>
                  )}

                  {/* Navigation buttons — sticky at the bottom of the
                      panel. The leftmost chevron toggles the scroll-lock so
                      the user can unlock panel scrolling on touch devices
                      (defaults to locked so accidental swipes don't scroll
                      the page). Visible only on mobile/tablet (lg-) since
                      the desktop sidebar always scrolls naturally. */}
                  {file && (
                    <div className="sticky bottom-0 -mx-1 px-1 pt-3 pb-1 mt-auto flex gap-2 bg-gradient-to-t from-slate-50 via-slate-50 to-slate-50/0 dark:from-slate-950 dark:via-slate-950 dark:to-slate-950/0 z-10">
                      <button
                        onClick={() => setScrollUnlocked(u => !u)}
                        className={`lg:hidden shrink-0 px-3 py-2.5 rounded-xl font-bold text-sm border transition-colors flex items-center justify-center ${
                          scrollUnlocked
                            ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-400 text-amber-600 dark:text-amber-400'
                            : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'
                        }`}
                        title={scrollUnlocked ? 'Scroll-Sperre aktivieren' : 'Scroll-Sperre aufheben'}
                        aria-label={scrollUnlocked ? 'Scroll-Sperre aktivieren' : 'Scroll-Sperre aufheben'}
                      >
                        {scrollUnlocked ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                      {currentStep > 1 && (
                        <button
                          onClick={() => setCurrentStep(currentStep - 1)}
                          className="flex-1 px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl font-bold text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center justify-center gap-2"
                        >
                          <ArrowLeft size={14} />
                          {t("orthesisEditor.previousStep")}
                        </button>
                      )}
                      {currentStep < 6 && currentStep >= 1 && (
                        <button
                          onClick={() => {
                            // Leaving Step 5 (Lochmuster) → CSG cut now. The
                            // cutShells useMemo runs synchronously during the
                            // next render and blocks the main thread for a
                            // few seconds on complex models, so we surface a
                            // ProcessingOverlay first, then defer the trigger
                            // through a setTimeout chain so React actually
                            // paints the overlay BEFORE the freeze.
                            if (currentStep === 5 && holePatternEnabled && !holePatternApplied) {
                              // Show overlay immediately, then defer the CSG
                              // trigger via rAF so React paints the overlay
                              // before the (slow) cutShells effect starts.
                              // The step advance + overlay clear happen via
                              // handleCutProgress when the per-hole progress
                              // callback hits 100.
                              setProcessingPhase('cutting');
                              setProcessingProgress(0);
                              setIsProcessingHoles(true);
                              requestAnimationFrame(() => {
                                requestAnimationFrame(() => {
                                  setHolePatternApplied(true);
                                });
                              });
                              return;
                            }
                            // Re-entering Step 5 from below should reset applied flag
                            // so user can adjust parameters and re-trigger.
                            if (currentStep === 4) {
                              setHolePatternApplied(false);
                            }
                            setCurrentStep(currentStep + 1);
                          }}
                          className="flex-1 px-4 py-2.5 bg-[#0f7699] dark:bg-[#4fc3f7] text-white dark:text-slate-900 rounded-xl font-bold text-sm hover:bg-[#0f7699]/90 dark:hover:bg-[#4fc3f7]/90 transition-colors flex items-center justify-center gap-2"
                        >
                          {t("orthesisEditor.nextStep")}
                          <ArrowRight size={14} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  );
}

export default PageOrthesisEditor;
